import {Vue as _Vue} from "vue-class-component";
import fetchIntercept from 'fetch-intercept';
import dayjs from "dayjs";
import {
    checkIfStringIsValidISODate,
    convertParamsToObject,
    formatDataTableBeforeSave,
    getDeploymentStage,
    getRelationsToSave,
    getRemovedIdsFromDataTable,
    getValidationErrorsFromDataTable,
    hoursBetween, isDev,
    isDevelopment,
    isLiftbaseUrl,
    LSK_ACCESS_JWT, LSK_ACCESS_JWT_OTHER,
    LSK_REFRESH_JWT, LSK_REFRESH_JWT_OTHER, sortDictionaryOnKeys, omitWarning, isLocalhost, getHasuraErrorKeyAndParams
} from "@/utils";
import {decode} from "jsonwebtoken";
import {getGqlMutationDocument, gqlQuery, gqlQueryRaw} from "@/apollo-graphql/utils";
import gql from "graphql-tag";
import {DocumentNode} from "graphql";
import UserService from "@/services/user-service";
import {hasAnyDashboardAccess, hasDashboardAccess} from "@/services/dashboard-access";
import _, {isArray, isEmpty, isObject, toNumber} from "lodash";
import auditLogService, {AuditLog, AuditLogType} from "@/services/audit-log-service";
import {diff_match_patch} from "diff-match-patch";
import * as XLSX from "xlsx";
import SelectUsersGroups from "@/components/common/select/select-users-groups.vue";
import {ForwardType} from "@/services/timline-actions";
import CurrencyService from "@/services/currency-service";
import {
    capApproversAfterLimitCheck,
    cascadeRequiredByApprovalLimit,
    isMaxApproversConfigured,
} from "@/services/approval-max-approvers";
import {prependCreatorOrFallbackApprovers} from "@/services/approval-creator-fallback";

const jexcelStyle = require('jexcel/dist/jexcel.css');
const jexcel = require('jexcel');
const LB_PAGE_TITLE_DATA_ATTRIBUTE = "data-lb-page-title";
const LOGIN_TENANTS_STORAGE_KEY_PREFIX = "liftbase-login-tenants:";
const MULTI_TENANT_LOGIN_FLAG_KEY_PREFIX = "liftbase-login-multi-tenant:";
const FRONTEND_CONFIG_STORAGE_KEY_PREFIX = "liftbase-configuration:";
const TENANT_LANGUAGE_STORAGE_KEY_PREFIX = "liftbase-main-language:";

const FRONTEND_CONFIG_GQL = gql`
    query GetConfiguration{
        list: configuration(order_by: {id:asc}, where:{model: {_neq: "password"}}){
            id
            key
            value
            model
            lb_user{
                id
                name
                is_group
                user_addresses (where: {active: {_eq: true}}){
                    id
                    lb_user_id
                    name
                    address
                    zipcode
                    consignee
                    city
                    complement
                    dropoff_location
                    country
                    active
                }
            }
            supplier{
                id
                max_approvers
                name
                email
                supplier_approvers (where: { lb_user: { active: { _eq: true }, blocked: {_eq: false}} }) {
                    supplier_id
                    required
                    lb_user_id
                    lb_user {
                        name
                        approval_limit
                        currency_approval_limit
                        priority_approval_limit
                        active
                        is_group
                        task_group_users {
                            lb_user {
                                id
                                name
                                approval_limit
                                currency_approval_limit
                            }
                        }
                    }
                }
            }
        }

        tenant: tenant {
            default_decimal_places
            ai_enabled
        }
    }
`

const TENANT_LANGUAGE_GQL = gql`
    query GetTenantLanguageGql{
        tenant {
            main_language
        }
    }
`

let frontendConfigLoadPromise: Promise<any[]> | null = null
let frontendConfigLoadStorageKey: string | null = null
let tenantLanguageLoadPromise: Promise<string> | null = null
let tenantLanguageLoadStorageKey: string | null = null

function getFrontendCacheTenantId() {
    return `${UserService.getCurrentUser()?.jwtUser?.tenantId || ""}`.trim()
}

function getFrontendConfigStorageKey(tenantId: string = getFrontendCacheTenantId()) {
    return tenantId ? `${FRONTEND_CONFIG_STORAGE_KEY_PREFIX}${tenantId}` : "configuration"
}

function getTenantLanguageStorageKey(tenantId: string = getFrontendCacheTenantId()) {
    return tenantId ? `${TENANT_LANGUAGE_STORAGE_KEY_PREFIX}${tenantId}` : "main_language"
}

function parseFrontendConfigCache(raw: string | null) {
    if (raw == null) return null

    try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch (_e) {
        return null
    }
}

function buildFrontendConfigurations(result: any) {
    const decimalPlacesConfig = {
        key: 'default_decimal_places',
        value: result.data?.tenant?.[0].default_decimal_places ?? 2
    }
    const aiEnabledConfig = {
        key: 'ai_enabled',
        value: result.data?.tenant?.[0].ai_enabled ?? false
    }

    return [...(result.data?.list ?? []), decimalPlacesConfig, aiEnabledConfig]
}

function readStoredFrontendConfigurations(tenantId: string = getFrontendCacheTenantId()) {
    const storageKey = getFrontendConfigStorageKey(tenantId)
    const cached = parseFrontendConfigCache(sessionStorage.getItem(storageKey))
    if (cached != null) return cached

    sessionStorage.removeItem("configuration")
    return null
}

function readStoredTenantLanguage(tenantId: string = getFrontendCacheTenantId()) {
    const storageKey = getTenantLanguageStorageKey(tenantId)
    const cached = sessionStorage.getItem(storageKey)
    if (cached != null && cached !== '') return cached

    sessionStorage.removeItem("main_language")
    return null
}

export function preloadFrontendConfig(force: boolean = false): Promise<any[]> {
    const currentUser = UserService.getCurrentUser()?.jwtUser
    if (!currentUser?.userId) return Promise.resolve([])

    const tenantId = `${currentUser.tenantId || ""}`.trim()
    const storageKey = getFrontendConfigStorageKey(tenantId)

    if (force) sessionStorage.removeItem(storageKey)
    else {
        const cached = readStoredFrontendConfigurations(tenantId)
        if (cached != null) return Promise.resolve(cached)
    }

    if (frontendConfigLoadPromise && frontendConfigLoadStorageKey === storageKey) {
        return frontendConfigLoadPromise
    }

    frontendConfigLoadStorageKey = storageKey
    frontendConfigLoadPromise = gqlQueryRaw(FRONTEND_CONFIG_GQL)
        .then((result: any) => {
            const configs = buildFrontendConfigurations(result)
            sessionStorage.setItem(storageKey, JSON.stringify(configs))
            return configs
        })
        .finally(() => {
            if (frontendConfigLoadStorageKey === storageKey) {
                frontendConfigLoadPromise = null
                frontendConfigLoadStorageKey = null
            }
        })

    return frontendConfigLoadPromise
}

export function preloadTenantLanguage(force: boolean = false): Promise<string> {
    const currentUser = UserService.getCurrentUser()?.jwtUser
    if (!currentUser?.userId) return Promise.resolve('de')

    const tenantId = `${currentUser.tenantId || ""}`.trim()
    const storageKey = getTenantLanguageStorageKey(tenantId)

    if (force) sessionStorage.removeItem(storageKey)
    else {
        const cached = readStoredTenantLanguage(tenantId)
        if (cached != null) return Promise.resolve(cached)
    }

    if (tenantLanguageLoadPromise && tenantLanguageLoadStorageKey === storageKey) {
        return tenantLanguageLoadPromise
    }

    tenantLanguageLoadStorageKey = storageKey
    tenantLanguageLoadPromise = gqlQueryRaw(TENANT_LANGUAGE_GQL)
        .then((result: any) => {
            const mainLanguage = result.data?.tenant?.[0]?.main_language || 'de'
            sessionStorage.setItem(storageKey, mainLanguage)
            return mainLanguage
        })
        .finally(() => {
            if (tenantLanguageLoadStorageKey === storageKey) {
                tenantLanguageLoadPromise = null
                tenantLanguageLoadStorageKey = null
            }
        })

    return tenantLanguageLoadPromise
}

function readStoredLbPageTitle() {
    return document.documentElement.getAttribute(LB_PAGE_TITLE_DATA_ATTRIBUTE) || ""
}

function writeStoredLbPageTitle(title: string) {
    document.documentElement.setAttribute(LB_PAGE_TITLE_DATA_ATTRIBUTE, title)
}

function hasMultipleLoginTenantsInSession(email?: string) {
    const normalizedEmail = (email || "").trim().toLowerCase()
    if (!normalizedEmail) return false

    if (localStorage.getItem(`${MULTI_TENANT_LOGIN_FLAG_KEY_PREFIX}${normalizedEmail}`) === "true") {
        return true
    }

    try {
        const raw = sessionStorage.getItem(`${LOGIN_TENANTS_STORAGE_KEY_PREFIX}${normalizedEmail}`)
        const tenants = raw ? JSON.parse(raw) : []
        return Array.isArray(tenants) && tenants.length > 1
    } catch (_e) {
        return false
    }
}

export function clearFrontendConfigCache() {
    Object.keys(sessionStorage).forEach((key: string) => {
        if (key === "configuration" ||
            key === "main_language" ||
            key.startsWith(FRONTEND_CONFIG_STORAGE_KEY_PREFIX) ||
            key.startsWith(TENANT_LANGUAGE_STORAGE_KEY_PREFIX)) {
            sessionStorage.removeItem(key)
        }
    })
    frontendConfigLoadPromise = null
    frontendConfigLoadStorageKey = null
    tenantLanguageLoadPromise = null
    tenantLanguageLoadStorageKey = null
}


function logout() {
    if (isInternPage()) {
        localStorage.setItem('redirectTo', window.location.hash)
        setIfFromPunchIn()
        saveLoginInfoFromUrl()
    }
    clearFrontendConfigCache()
    localStorage.removeItem(LSK_REFRESH_JWT_OTHER)
    localStorage.removeItem(LSK_ACCESS_JWT_OTHER)
    localStorage.removeItem(LSK_REFRESH_JWT)
    localStorage.removeItem(LSK_ACCESS_JWT)
    localStorage.removeItem('onboarding-tour')
    // @ts-ignore
    window.location = "#/login";
}

export function setIfFromPunchIn() {
    if (isFromPunchIn()) localStorage.setItem('fromPunchIn', 'true')
}

export function isFromPunchIn() {
    return window.location.href.indexOf("requiresAuthByCredentials") >= 0
}

function saveLoginInfoFromUrl() {
    const url = window.location.hash;
    const loginInfo = convertParamsToObject(url.substring(url.indexOf('?')))
    if (loginInfo?.email) localStorage.setItem('emailToLogin', loginInfo.email)
    if (loginInfo?.tenant) localStorage.setItem('tenantToLogin', loginInfo.tenant)
}

function refreshToken(endpoint: string, from: string, to: string) {
    const token = localStorage.getItem(from)
    if (token) {
        if (isJwtExpired(token)) {
            logout();
            return Promise.resolve()
        }

        const config = {
            method: "POST",
            headers: {
                "Authorization": "Bearer " + token
            }
        }
        return fetch(endpoint, config)
            .then((response: any) => {
                if (response.ok) {
                    return response.json()
                }
            })
            .then((data: any) => {
                if (data == null) {
                    if (isRefreshRequest(endpoint)) logout()
                    return
                }
                localStorage.setItem(to, data.jwt)
            })
    } else if (isRefreshRequest(endpoint)) {
        logout()
        return new Promise((resolve: any) => resolve())
    } else {
        return new Promise((resolve: any) => resolve())
    }
}

function isRefreshRequest(endpoint: string) {
    return endpoint.indexOf('refresh-token') >= 0
}

/**
 * Refresh and exchange the refresh-token for a short-lived access-token for data access
 */
export function refreshAccessToken() {
    return refreshToken("/api/auth/access-token", LSK_REFRESH_JWT, LSK_ACCESS_JWT)
}

export function refreshAccessTokenOther() {
    return refreshToken("/api/auth/access-token", LSK_REFRESH_JWT_OTHER, LSK_ACCESS_JWT_OTHER)
}

/**
 * Refresh and exchange the refresh-token for a new refresh-token so the user "is not logged out"
 */
export function refreshRefreshToken(typeToken: string) {
    return refreshToken("/api/auth/refresh-token", typeToken, typeToken)
}

export function lbXhrAuthentication() {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method: string, url: string) {
        const token = localStorage.getItem(LSK_ACCESS_JWT_OTHER) || localStorage.getItem(LSK_ACCESS_JWT)

        //@ts-ignore
        origOpen.apply(this, arguments);

        if (isLiftbaseUrl(url)) {
            this.setRequestHeader("Authorization", "Bearer " + token)
        }
    };

    fetchIntercept.register({
        request(url: string, config: any): any {
            if (isLiftbaseUrl(url)) {
                config = config || {}
                config.headers = config.headers || {}

                if (!config.headers["Authorization"] && url.indexOf('login') < 0) {
                    const tokenOther = localStorage.getItem(LSK_ACCESS_JWT_OTHER)
                    if (!tokenOther || isJwtExpired(tokenOther)) refreshAccessTokenOther()


                    const token = localStorage.getItem(LSK_ACCESS_JWT) as string
                    if (token) {
                        if (isJwtExpired(token)) {
                            // start the refresh process - this request will fail, but thats ok
                            refreshAccessToken()
                        }

                        config.headers["Authorization"] = "Bearer " + (tokenOther || token)
                        setHasuraDefaultRole(config, !tokenOther)
                    } else if (localStorage.getItem(LSK_REFRESH_JWT) || localStorage.getItem(LSK_REFRESH_JWT_OTHER)) {
                        void refreshAccessToken()
                        void refreshAccessTokenOther()
                    } else {
                        logout()
                    }
                }

            }

            return [url, config]
        }
    })

    // if the dont have any token, redirect to login
    const anyToken = !!(localStorage.getItem(LSK_ACCESS_JWT) || localStorage.getItem(LSK_REFRESH_JWT))
    if (!isLoginPage() && !anyToken) {
        logout();
        return new Promise((resolve: any) => resolve())
    }

    checkAndRefreshToken(LSK_REFRESH_JWT)
    checkAndRefreshToken(LSK_REFRESH_JWT_OTHER)

    /**
     * refresh the login token every 10 minutes
     */
    setInterval(refreshAccessToken, 1000 * 60 * 10)
    setInterval(checkAndRefreshAccessTokenOther, 1000 * 60 * 10)
    return refreshAccessToken()
}

export function getLbRequestHeaders(url: string, headers: any = {}) {
    const requestHeaders = {...(headers || {})} as any
    if (isLiftbaseUrl(url) && !requestHeaders["Authorization"] && url.indexOf('login') < 0) {
        const tokenOther = localStorage.getItem(LSK_ACCESS_JWT_OTHER)
        const token =
            (!!tokenOther && !isJwtExpired(tokenOther))
                ? tokenOther
                : localStorage.getItem(LSK_ACCESS_JWT)
        if (token) requestHeaders["Authorization"] = "Bearer " + token
    }

    return requestHeaders
}

function shouldRetryAfterAuthError(url: string, status: number) {
    if (status !== 401 && status !== 403) return false
    if (!isLiftbaseUrl(url)) return false
    if (url.indexOf('/api/auth/access-token') >= 0) return false
    if (url.indexOf('login') >= 0) return false
    return true
}

async function refreshAccessTokensForRetry() {
    const refreshes = []

    if (localStorage.getItem(LSK_REFRESH_JWT_OTHER)) {
        const refreshOther = refreshAccessTokenOther()
        if (refreshOther) refreshes.push(refreshOther)
    }

    if (localStorage.getItem(LSK_REFRESH_JWT)) {
        const refreshMain = refreshAccessToken()
        if (refreshMain) refreshes.push(refreshMain)
    }

    if (refreshes.length) await Promise.all(refreshes)
}

export function lbFetchRaw(
    {
        url = "",
        method = "POST",
        headers = {
            "Content-Type": "application/json"
        },
        data = null as any
    }: {
        url?: string,
        method?: string,
        headers?: Record<string, string>,
        data?: any
    }
) {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData
    const requestHeaders = {...(headers || {})}
    if (isFormData) delete requestHeaders["Content-Type"]
    const sendRequest = () => fetch(url, {
        method: method,
        headers: getLbRequestHeaders(url, requestHeaders),
        body: data && (isFormData ? data : JSON.stringify(data))
    })

    return sendRequest().then(async response => {
        if (!shouldRetryAfterAuthError(url, response.status)) return response

        try {
            await refreshAccessTokensForRetry()
        } catch (_e) {
            return response
        }

        return sendRequest()
    })
}

function checkAndRefreshAccessTokenOther() {
    const token = localStorage.getItem(LSK_REFRESH_JWT_OTHER) || localStorage.getItem(LSK_ACCESS_JWT_OTHER)
    if (token) checkAndRefreshAccessTokenOther()
}

function checkAndRefreshToken(typeToken: string) {
    const refreshJwt = localStorage.getItem(typeToken) as string;
    if (refreshJwt) {
        const refreshTokenExpiration = getJwtExpiration(refreshJwt)
        const now = new Date()
        const number = hoursBetween(refreshTokenExpiration, now);
        if (number < 32) refreshRefreshToken(typeToken)
    }
}

function setHasuraDefaultRole(config: any, loggedUser: boolean = true) {
    const user = loggedUser ? UserService.getLoggedUser() : UserService.getCurrentUser()
    const isOperator = user?.jwtUser?.roles?.find((role: any) => role == 'user-operator')
    if (isOperator && isAccessingFromOperatorArea()) {
        config.headers["x-hasura-role"] = "user-operator"
    }
    const isAdmin = user?.jwtUser?.roles?.find((role: any) => role == 'user-admin')
    if (isAdmin || isOperator) return

    if (!isAccessingFromAdminArea()) {
        config.headers["x-hasura-role"] = "user"
    } else {
        const requiresMasterdataRole = getRequiredMasterdataRoleFromLocation()
        const hasRequiredMasterdataPermission = !!requiresMasterdataRole && !!user?.jwtUser?.roles?.includes(requiresMasterdataRole)
        config.headers["x-hasura-role"] = hasRequiredMasterdataPermission ? "masterdata" : "user"
    }
}

export function isAccessingFromOperatorArea() {
    return window.location.hash.indexOf('operator') >= 0
}

export function isAccessingFromAdminArea() {
    return window.location.hash.indexOf('admin') >= 0
}

export function isOperatorOnlyFeature() {
    return window.location.hash.indexOf('ai-prompt') >= 0
}

function getRequiredMasterdataRoleFromLocation(): string | null {
    const hash = window.location.hash || ''
    const route = hash.toLowerCase()

    if (route.indexOf('#/admin/client') >= 0) return 'masterdata-edit-clients'
    if (route.indexOf('#/admin/cost-center') >= 0) return 'masterdata-edit-cost-centers'
    if (route.indexOf('#/admin/posting-key') >= 0) return 'masterdata-edit-posting-keys'
    if (route.indexOf('#/admin/department') >= 0) return 'masterdata-edit-departments'
    if (route.indexOf('#/admin/supplier') >= 0) return 'masterdata-edit-suppliers'
    if (route.indexOf('#/admin/business-partner') >= 0) return 'masterdata-edit-business-partners'
    if (route.indexOf('#/admin/group-product') >= 0) return 'masterdata-edit-product-groups'
    if (route.indexOf('#/admin/product') >= 0) return 'masterdata-edit-products'

    return null
}

/**
 * Checks if the given jwt
 */
export function isJwtExpired(token: string) {
    const validTo = getJwtExpiration(token)
    const now = new Date()
    return now > validTo
}

export function getJwtExpiration(token: string) {
    const decoded = decode(token)
    const exp = decoded["exp"]
    return new Date(exp * 1000)
}

function isLoginPage() {
    return window.location.hash.indexOf('login') >= 0;
}

function isInternPage() {
    return window.location.hash.indexOf('login') < 0
        && window.location.hash.indexOf('logout') < 0
        && window.location.hash.indexOf('graphql') < 0
        && getDeploymentStage() != null
}

export class Vue extends _Vue {

    /**
     * "element-plus"-based notify service
     */
    $message: any | {
        title: string,
        message: string,
        type: string
    }
    $prompt: any
    $confirm: any
    $alert: any

    // i18n
    $t: any
    $i18n: any

    languages = [
        {label: "Deutsch", value: "de"},
        {label: "English", value: "en"},
        {label: "Italiano (Beta)", value: "it"},
        {label: "Čeština (Beta)", value: "cz"},
        {label: "Slovenčina (Beta)", value: "sk"},
    ]

    txtBeforeRouteLeave = "Ihre Änderungen werden eventuell nicht gespeichert. Sind Sie sicher, dass Sie fortfahren wollen?";

    jExcelObj: any = null
    jExcelOptions: any
    jExcelColumns: any
    jExcelValidationErrors: any = []

    viewMode = 'list'
    search: string = ""
    searchRegEx: RegExp = /.*/

    /**
     * used to prevent user leaving the page if it has unsaved changes
     */
    static hasUnsavedChanges: boolean = false;


    /**
     * "mitt"-based event bus
     */
    emitter: any

    /**
     * Global currency config
     */
    currencyConfig = {
        locale: 'de-DE',
        valueRange: {
            "min": 0
        },
        currency: "EUR",
        currencyDisplay: "symbol",
        precision: {
            "min": 2,
            "max": 4
        },
        hideCurrencySymbolOnFocus: true,
        hideGroupingSeparatorOnFocus: true,
        hideNegligibleDecimalDigitsOnFocus: true,
        autoDecimalDigits: false,
        useGrouping: true,
        accountingSign: false,
        default_decimal_places: 2
    }

    currencies: any[] = (() => {
        try {
            const codes: string[] = ((Intl as any).supportedValuesOf?.('currency')) ?? ['EUR', 'USD']
            const uniqueCodes = Array.from(new Set(codes))
            const locale = 'de-DE'
            const items = uniqueCodes.map((code: string) => {
                try {
                    const formatter = new Intl.NumberFormat(locale, {style: 'currency', currency: code})
                    const parts = (formatter.formatToParts(1234.56) || []) as Intl.NumberFormatPart[]
                    const currencyPartIndex = parts.findIndex(p => p.type === 'currency')
                    const integerIndex = parts.findIndex(p => p.type === 'integer')
                    const label = parts.find(p => p.type === 'currency')?.value || code
                    const prefix = currencyPartIndex > -1 && integerIndex > -1 ? currencyPartIndex < integerIndex : false
                    const suffix = currencyPartIndex > -1 && integerIndex > -1 ? currencyPartIndex > integerIndex : true
                    return {
                        id: code,
                        label,
                        value: code,
                        name: code,
                        prefix,
                        suffix
                    }
                } catch (e) {
                    return {id: code, label: code, value: code, name: code, suffix: true}
                }
            })

            const ensure = (code: string, label: string, extra: any = {}) => {
                if (!items.find(i => i.value === code)) items.push({id: code, label, value: code, name: code, ...extra})
            }
            ensure('EUR', '€', {suffix: true})
            ensure('USD', 'US$', {prefix: true})

            items.sort((a: any, b: any) => a.value.localeCompare(b.value))
            return items
        } catch (e) {
            return [
                {id: 'EUR', label: '€', value: 'EUR', name: 'EUR', suffix: true},
                {id: 'USD', label: 'US$', value: 'USD', name: 'USD', prefix: true},
            ]
        }
    })()

    getCurrencyConfig(currency: string) {
        const currencyConfig = this.getCurrency(currency)
        const suffix = currencyConfig?.suffix ? ' ' + currencyConfig?.label : ''
        const prefix = currencyConfig?.prefix ? currencyConfig?.label + ' ' : ''
        return {
            ...this.currencyConfig,
            currency: currency ?? 'EUR',
            prefix,
            suffix,
        }
    }

    getCurrency(currency: string) {
        const found = _.find(this.currencies as any[], (item: any) => item?.value == currency)
        return found || {id: currency, label: currency, value: currency, name: currency, suffix: true}
    }

    allFinFieldsToValidate = [
        "cost_center_id", "ledger_account_id", "profit_center_id", "d365_department_id",
        "employee_id", "branch_id", "d365_vehicle_id", "d365_sales_channel_id", "d365_area_id",
        "d365_labor_wert_id", "d365_project_id"
    ]

    /**
     * START: GLOBAL config
     * */
    findConfigByKey(key: string) {
        return gqlQueryRaw(this.globalConfigsGql, {key}).then((result: any) => {
            return result.data.config[0]
        })
    }

    globalConfigsGql = gql`
        query getConfig(
            $key: String!
        ) {
            config:global_configuration(where: {key: {_eq: $key}}) {
                id
                key
                type
                value
                group
                label
            }
        }
    `
    configurationIsLoading: any = false
    thereIsNoConfiguration: any = false
    tenantLanguageIsLoading: any = false

    get configurations() {
        if (!this.isLogged()) {
            clearFrontendConfigCache()
            return []
        }

        const configs = readStoredFrontendConfigurations()
        if (configs != null) {
            const decimalPlaces = configs.find((item: any) => item.key == 'default_decimal_places')?.value
            this.currencyConfig.default_decimal_places = decimalPlaces ?? 2
            return configs
        } else if (!this.configurationIsLoading) {
            this.configurationIsLoading = true
            preloadFrontendConfig().then((loadedConfigs: any[]) => {
                const decimalPlaces = loadedConfigs.find((item: any) => item.key == 'default_decimal_places')?.value
                this.currencyConfig.default_decimal_places = decimalPlaces ?? 2
                this.refreshLbTitle()
            }).catch(() => {
            }).finally(() => {
                this.configurationIsLoading = false
                this.$forceUpdate()
            })
        }
        return []
    }

    get tenantLanguage() {
        if (!this.isLogged()) {
            clearFrontendConfigCache()
            return 'de'
        }

        const mainLanguage = readStoredTenantLanguage()
        if (mainLanguage != null && mainLanguage !== '') return mainLanguage
        else if (!this.tenantLanguageIsLoading) {
            this.tenantLanguageIsLoading = true
            preloadTenantLanguage().then(() => {
            }).catch(() => {
            }).finally(() => {
                this.tenantLanguageIsLoading = false
                this.$forceUpdate()
            })
        }
        return 'de'
    }

    refreshConfigurations() {
        if (!isAccessingFromOperatorArea()) {
            sessionStorage.removeItem(getFrontendConfigStorageKey())
            sessionStorage.removeItem("configuration")
            this.thereIsNoConfiguration = false
        }
        return this.configurations
    }

    refreshMainLanguage() {
        if (!isAccessingFromOperatorArea()) {
            sessionStorage.removeItem(getTenantLanguageStorageKey())
            sessionStorage.removeItem("main_language")
        }
        return this.tenantLanguage
    }

    get defaultDecimalPlaces() {
        const config = this.configurations?.find((item: any) => item.key == 'default_decimal_places')
        return config?.value || 2
    }

    get aiEnabled() {
        const config = this.configurations?.find((item: any) => item.key == 'ai_enabled')
        return !!config?.value
    }

    configGql = gql`
        query GetConfiguration{
            list: configuration(order_by: {id:asc}, where:{model: {_neq: "password"}}){
                id
                key
                value
                model
                lb_user{
                    id
                    name
                    is_group
                    user_addresses (where: {active: {_eq: true}}){
                        id
                        lb_user_id
                        name
                        address
                        zipcode
                        consignee
                        city
                        complement
                        dropoff_location
                        country
                        active
                    }
                }
                supplier{
                    id
                    max_approvers
                    name
                    email
                    supplier_approvers (where: { lb_user: { active: { _eq: true }, blocked: {_eq: false}} }) {
                        supplier_id
                        required
                        lb_user_id
                        lb_user {
                            name
                            approval_limit
                            currency_approval_limit
                            priority_approval_limit
                            active
                            is_group
                            task_group_users {
                                lb_user {
                                    id
                                    name
                                    approval_limit
                                    currency_approval_limit
                                }
                            }
                        }
                    }
                }
            }

            tenant: tenant {
                default_decimal_places
                ai_enabled
            }
        }
    `


    tenantLanguageGql = gql`
        query GetTenantLanguageGql{
            tenant {
                main_language
            }
        }
    `

    /**
     * END: GLOBAL config
     * */

    mounted() {
        this.setCommonTexts()
    }

    setCommonTexts() {
        this.txtBeforeRouteLeave = this.$t('txt_before_leave_page')
    }

    /**
     * START: Tour - Onboarding
     * */
    $tours: any

    startTourIfFirstTime(tourName: string, callbackFn: any = null) {
        if (!isAccessingFromOperatorArea()) {
            this.isTourGlobalActived().then((active: any) => {
                if (active) {
                    gqlQueryRaw(
                        this.toursGql,
                        {page: tourName, userId: this.getCurrentUser()?.jwtUser.userId}
                    ).then((result: any) => {
                        if (result?.data?.user?.show_tour && result?.data?.tours?.length == 0) {
                            this.stopTours(tourName)
                            this.$tours[tourName]?.start()
                            if (callbackFn) callbackFn()
                        }
                    })
                }
            })
        }
    }

    isTourGlobalActived() {
        const active = localStorage.getItem('onboarding-tour')
        if (active == 'true') return new Promise((resolve: any) => resolve(true));
        else if (active == 'false') return new Promise((resolve: any) => resolve(false));
        return this.findConfigByKey('onboarding-tour').then((config: any) => {
            localStorage.setItem('onboarding-tour', config.value)
            if (config.value == 'true') return new Promise((resolve: any) => resolve(true));
            return new Promise((resolve: any) => resolve(false));
        })
    }

    stopTours(exceptName?: string) {
        _.keys(this.$tours)?.forEach((name, index) => {
            if ((!exceptName || name !== exceptName) && this.isTourRunning(name)) {
                this.forceStopTour(name)
            }
        })
    }

    isTourRunning(name: string) {
        return this.$tours[name]?.isRunning?.value == true
    }

    forceStopTour(name: string) {
        this.$tours[name]?.finish()
        this.emitter.emit("stop-tour", {name: name})
    }

    getTourTarget(steps: any[]) {
        return steps.find((step: any) => document.querySelector(step.target))
    }

    toursGql = gql`
        query getTour(
            $page: String!
            $userId: bigint!
        ) {
            tours:tour(where: {page: {_eq: $page}}) {
                id
            }

            user: lb_user_by_pk(id: $userId){
                show_tour
            }
        }
    `

    scrollTop() {
        this.$el.parentElement.scrollTop = 0
    }

    /**
     * END: Tour - Onboarding
     * */


    private getUserGql = gql`
        query findUser($id: bigint!) {
            user:lb_user_by_pk(id:  $id) {
                id
                email
                name
                lang
                approval_limit
                currency_approval_limit
                priority_approval_limit
                tenant {
                    name
                }
                department_id
                department {
                    id
                    max_approvers
                    add_creator_or_fallback
                    approval_fallback_user_id
                    approval_fallback_user {
                        id
                        name
                        approval_limit
                        currency_approval_limit
                        priority_approval_limit
                        active
                        blocked
                        is_group
                        task_group_users {
                            lb_user {
                                id
                                name
                                approval_limit
                                currency_approval_limit
                            }
                        }
                    }
                    code
                    active
                    name: translated_name
                    responsible {
                        id
                        name
                    }
                    user_clearing_id
                    user_clearing {
                        id
                        name
                    }
                    user_select_product_id
                    user_select_product {
                        id
                        name
                    }
                    user_send_order_id
                    user_send_order {
                        id
                        name
                    }
                    user_consignee_id
                    user_consignee {
                        id
                        name
                        user_addresses (where: {active: {_eq: true}}){
                            id
                            lb_user_id
                            name
                            address
                            zipcode
                            consignee
                            city
                            complement
                            dropoff_location
                            country
                            active
                        }
                    }
                    department_approvers (where: { lb_user: { active: { _eq: true }, blocked: {_eq: false}} }) {
                        department_id
                        required
                        lb_user_id
                        lb_user {
                            name
                            id
                            approval_limit
                            currency_approval_limit
                            priority_approval_limit
                            active
                            is_group
                            task_group_users {
                                lb_user {
                                    id
                                    name
                                    approval_limit
                                    currency_approval_limit
                                }
                            }
                        }
                    }
                }
                cost_center_id
                cost_center {
                    id
                    max_approvers
                    add_creator_or_fallback
                    approval_fallback_user_id
                    approval_fallback_user {
                        id
                        name
                        approval_limit
                        currency_approval_limit
                        priority_approval_limit
                        active
                        blocked
                        is_group
                        task_group_users {
                            lb_user {
                                id
                                name
                                approval_limit
                                currency_approval_limit
                            }
                        }
                    }
                    name: translated_name
                    number
                    active
                    budgets {
                        id
                        name
                        not_allocated
                        ledger_account_id
                        start
                        end
                        active
                        value
                        ledger_account {
                            name
                        }
                    }
                    cost_center_approvers (where: { lb_user: { active: { _eq: true }, blocked: {_eq: false}} }) {
                        cost_center_id
                        lb_user_id
                        required
                        lb_user {
                            name
                            id
                            approval_limit
                            currency_approval_limit
                            priority_approval_limit
                            active
                            is_group
                            task_group_users {
                                lb_user {
                                    id
                                    name
                                    approval_limit
                                    currency_approval_limit
                                }
                            }
                        }
                    }
                }
                roles{
                    role
                }
                client_id
                client {
                    client_number
                    responsible {
                        id
                        name
                    }
                    user_clearing_id
                    user_clearing {
                        id
                        name
                    }
                    user_select_product_id
                    user_select_product {
                        id
                        name
                    }
                    user_send_order_id
                    user_send_order {
                        id
                        name
                    }
                    user_consignee_id
                    user_consignee {
                        id
                        name
                        user_addresses (where: {active: {_eq: true}}){
                            id
                            lb_user_id
                            name
                            address
                            zipcode
                            consignee
                            city
                            complement
                            dropoff_location
                            country
                            active
                        }
                    }
                    user_contract_clearing_id
                    user_contract_clearing {
                        id
                        name
                    }
                    user_invoice_clearing_id
                    user_invoice_clearing {
                        id
                        name
                    }
                    id
                    active
                    name
                    min_approval
                    default_product_tax
                    default_product_unit
                    address
                    zipcode
                    city
                    address_complement
                    address_name
                    consignee
                    dropoff_location
                    country
                    phone_country_code
                    phone_number
                    client_addresses(where: {active: {_eq: true}}){
                        id
                        address
                        zipcode
                        city
                        active
                        default
                        client_id
                        name
                        complement
                        consignee
                        dropoff_location
                        country
                        phone_country_code
                        phone_number
                    }
                }
                user_addresses(where: {active: {_eq: true}}){
                    id
                    lb_user_id
                    name
                    address
                    complement
                    active
                    consignee
                    country
                    phone_number
                    phone_country_code
                    dropoff_location
                    city
                    zipcode
                }
            }
        }
    `
    private getSimpleUserInfosGql = gql`
        query findUser($id: bigint!) {
            user:lb_user_by_pk(id:  $id) {
                id
                email
                name
                approval_limit
                currency_approval_limit
                tenant {
                    name
                }
                roles{
                    role
                }
            }
        }
    `

    lbTitle(title: string) {
        writeStoredLbPageTitle(title)
        document.title = this.buildLbTitle(title)
    }

    public lbMutateUX(promise: any, mutationDocument?: DocumentNode) {
        const that = this
        return promise.then((value: any) => {
            try {
                that.lbCheckDuplicateCreateResult && that.lbCheckDuplicateCreateResult(value, mutationDocument)
            } catch (e) {
                throw e
            }
            //@ts-ignore
            that.$message({
                title: "Gespeichert",
                message: that.$t('saved_success'),
                type: "success",
                duration: 1250
            })
            return value;
        }).catch((error: any) => {
            // If error was thrown intentionally to prevent generic error toast
            if (error && (error as any).__lbSilent) {
                throw error
            }
            let errorMessage = error.message || ''

            try {
                const {key, params} = getHasuraErrorKeyAndParams(errorMessage)
                errorMessage = that.$t(key, params) || errorMessage
            } catch (err) {
            }

            //@ts-ignore
            that.$message({
                title: "Fehler",
                message: that.$t('save_error') + ' ' + errorMessage,
                type: "error"
            })

            console.error(error)
            throw error;
        })
    }

    lbCheckDuplicateCreateResult(this: any, value: any, mutationDocument?: DocumentNode) {
        if (typeof this.lbOrgCheckDuplicateCreateResult === 'function') {
            const handled = this.lbOrgCheckDuplicateCreateResult(value, mutationDocument)
            if (handled) return
        }

        const insertResultKeys = this.getInsertOneMutationResultKeys(mutationDocument)
        for (const key of insertResultKeys) {
            const result = value?.data?.[key]
            if (result && typeof result === 'object' && 'id' in result &&
                (result.id === null || result.id === undefined)) {
                //@ts-ignore
                this.$message({
                    title: this.$t('error_title'),
                    message: this.$t('duplicate_exists_check_records'),
                    type: 'warning',
                    duration: 3000
                })

                if (typeof this.loading !== 'undefined') this.loading = false

                const error: any = new Error('DUPLICATE_OBJECT_EXISTS')
                error.__lbSilent = true
                throw error
            }
        }
    }

    private getInsertOneMutationResultKeys(mutationDocument?: DocumentNode): string[] {
        if (!mutationDocument) return []

        const resultKeys: string[] = []
        for (const definition of mutationDocument.definitions) {
            if (definition.kind !== 'OperationDefinition' || definition.operation !== 'mutation') continue

            for (const selection of definition.selectionSet.selections) {
                if (selection.kind !== 'Field' || !/^insert_.+_one$/.test(selection.name.value)) continue
                resultKeys.push(selection.alias?.value ?? selection.name.value)
            }
        }
        return resultKeys
    }

    lbMutate(this: any, mutation: any, data: any) {
        return this.lbMutateUX(mutation.mutate(data), getGqlMutationDocument(mutation))
    }

    lbMutateWithCustomHeader(this: any, mutation: any, variables: any, headers: any = {}) {
        return this.lbMutateUX(
            mutation.mutate(variables, {context: {headers}}),
            getGqlMutationDocument(mutation)
        )
    }


    lbFetch(
        this: Vue,
        {
            url = "",
            method = "POST",
            headers = {
                "Content-Type": "application/json"
            },
            data = null as any,
            successTitle = "",
            successMessage = "",
            errorTitle = "",
            errorMessage = "",
            showError = true,
            success = function (this: Vue, data: any): boolean | void {
                return false
            },
            error = function (this: Vue, error: any): boolean | void {
                return false
            }
        }
    ) {
        const that = this
        return lbFetchRaw({
            url,
            method,
            headers,
            data
        }).then(response => {
            if (response.ok) {
                return response.text()
            }

            if (!error.apply(that, [response]) && showError) {
                //@ts-ignore
                that.$message({
                    title: errorTitle || that.$t('error_title'),
                    message: errorMessage || that.$t('request_error'),
                    type: "error"
                })
            }
        }).then(result => {
            if (result == null) return

            let data
            try {
                data = JSON.parse(result)
            } catch (e) {
                data = {}
            }
            if (!success.apply(that, [data]) && successTitle) {
                //@ts-ignore
                that.$message({
                    title: successTitle,
                    message: successMessage,
                    type: "success"
                })
            }
        }).catch(exception => {
            if (!error.apply(that, [exception]) && showError) {
                //@ts-ignore
                that.$message({
                    title: errorTitle || that.$t('error_title'),
                    message: errorMessage || that.$t('request_error'),
                    type: "error"
                })
            }
        })
    }

    lbNowIso() {
        return dayjs().format("YYYY-MM-DD")
    }

    lbDateIso(date: string) {
        return dayjs(date).format("YYYY-MM-DD")
    }

    lbDate(date: string) {
        return dayjs(date).format("DD.MM.YYYY")
    }

    lbDateTime(date: string) {
        return dayjs(date).format("DD.MM.YYYY HH:mm")
    }

    lbNewWindow(
        this: Vue,
        {
            url = "",
            features = {
                scrollbars: "yes",
                status: "yes"
            },
            onClosed = (window: Window) => {
            },
            onOpen = (window: Window) => {
            },
            onError = () => {
            }
        }
    ) {
        const featuresString = Object.entries(features)
            .map((entry: any) => entry.key + "=" + entry.value)
            .join(",")

        const newWindow = window.open(url, '_blank', featuresString)
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
            onError()
            return
        }
        onOpen(newWindow)

        var timer = setInterval(function () {
            if (newWindow.closed) {
                clearInterval(timer);
                onClosed(newWindow)
            }
        }, 100);
    }

    isDev() {
        return isDev()
    }

    isLocalhost() {
        return isLocalhost()
    }

    confirmBeforeDelete(row: any, callback: any, msg?: string, extraParam?: any) {
        const text = msg ?? this.$t('admin_page.confirm_before_delete_msg', {id: row.id})
        this.$confirm(text, this.$t('delete'), {
            confirmButtonText: this.$t('delete'),
            confirmButtonClass: 'el-button--danger solid',
            cancelButtonText: this.$t('cancel'),
            center: true
        })
            .then(() => {
                if (extraParam) callback(row, extraParam)
                else callback(row)
            })
            .catch(() => {

            })
    }

    confirmBeforeDeleteWithInput(row: any, callback: any, msg?: string, msgError?: string) {
        const text = msg ?? this.$t('admin_page.confirm_before_delete_from_db_msg', {id: row.id})
        this.$prompt(text, this.$t('admin_page.confirm_before_delete_from_db_title'), {
            confirmButtonText: this.$t('admin_page.confirm_before_delete_from_db_btn'),
            confirmButtonClass: 'el-button--danger solid',
            cancelButtonText: this.$t('cancel'),
            center: true,
            dangerouslyUseHTMLString: !!msg
        })
            .then((input: any) => {
                if (row.name == input.value)
                    callback(row)
                else
                    this.$alert(msgError)
            })
            .catch(() => {

            })
    }

    confirmBeforeToggleBlock(row: any, callback: any) {
        let txt = row.blocked ?
            this.$t('admin_page.unblock_user_msg', {name: row.name}) :
            this.$t('admin_page.block_user_msg', {name: row.name})
        const txtBtn = row.blocked ? this.$t('admin_page.unblock_user_btn') : this.$t('admin_page.block_user_btn')
        const txtTitle = row.blocked ? this.$t('admin_page.unblock_user_title') : this.$t('admin_page.block_user_title')
        this.$confirm(txt, txtTitle, {
            confirmButtonText: txtBtn,
            confirmButtonClass: 'el-button--warning',
            cancelButtonText: this.$t('cancel'),
            center: true
        })
            .then(() => {
                callback(row)
            })
            .catch(() => {

            })
    }

    getCurrentUser() {
        return UserService.getCurrentUser()
    }

    getLoggedUser() {
        return UserService.getLoggedUser()
    }

    isLogged() {
        return !!this.getCurrentUser()?.jwtUser?.userId;
    }

    get userPermissions() {
        return this.getCurrentUser()?.jwtUser?.permissions;
    }

    get userLang() {
        return this.getCurrentUser()?.jwtUser?.lang
    }

    get tenantName() {
        return this.getCurrentUser()?.jwtUser?.tenantName
    }

    buildLbTitle(title: string) {
        const tenantName = this.getLbTitleTenantName()
        return ["liftbase", tenantName, title].filter(Boolean).join(" - ")
    }

    getLbTitleTenantName() {
        const tenantName = this.tenantName?.trim()
        const email = this.getCurrentUser()?.jwtUser?.userEmail
        if (!tenantName || !email) return ""
        // if (!this.isInsolvencyLawModulActive()) return ""
        return hasMultipleLoginTenantsInSession(email) ? tenantName : ""
    }

    refreshLbTitle() {
        const title = readStoredLbPageTitle()
        if (!title) return
        document.title = this.buildLbTitle(title)
    }

    joinArrayToString(array: any) {
        if (!array) return '{}'
        return `{${array.join(',')}}`
    }

    get isOperator() {
        return !!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-operator')
    }

    get isAdmin() {
        return !!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-admin')
    }

    get hasAccessToDashboard() {
        return this.hasAccessToProcurementDashboard
    }

    get hasAccessToAnyDashboard() {
        return hasAnyDashboardAccess(this.getCurrentUser()?.jwtUser?.roles ?? [])
    }

    get hasAccessToProcurementDashboard() {
        return hasDashboardAccess(this.getCurrentUser()?.jwtUser?.roles ?? [], 'procurement')
    }

    get hasAccessToInvoiceDashboard() {
        return hasDashboardAccess(this.getCurrentUser()?.jwtUser?.roles ?? [], 'invoice')
    }

    get hasAccessToOutgoingInvoiceDashboard() {
        return hasDashboardAccess(this.getCurrentUser()?.jwtUser?.roles ?? [], 'outgoingInvoice')
    }

    get hasAccessToEinvoiceDashboard() {
        return hasDashboardAccess(this.getCurrentUser()?.jwtUser?.roles ?? [], 'einvoice')
    }

    get hasAccessToSupplierDashboard() {
        return hasDashboardAccess(this.getCurrentUser()?.jwtUser?.roles ?? [], 'supplier')
    }

    get hasAccessToPrices() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-price'))
    }

    get hasAccessToAllProcurements() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-see-all-orders'))
    }

    get hasAccessToAllProcurementsBelongingToOwnCompany() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-see-all-orders-belonging-to-own-company'))
    }

    get hasAccessToAllProcurementsBelongingToOwnDepartment() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-see-all-orders-belonging-to-own-department'))
    }


    get hasAccessToOwnProcurementsOrIfInvolved() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => [
            'user-see-own-orders-and-involved',
            'user-see-orders-from-involved-invoices'
        ].includes(role)))
    }

    get hasAccessToInvoicesFromOwnOrders() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => [
            'user-see-invoices-from-own-orders'
        ].includes(role)))
    }

    get hasAccessToAllInvoices() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-see-all-invoices'))
    }

    get canEditInvoices() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-can-change-invoices'))
    }

    get hasAccessToAllOutgoingInvoices() {
        const roles = this.getCurrentUser()?.jwtUser?.roles ?? []
        return this.isAdmin || !!roles.find((role: any) => [
            'user-see-all-outgoing-invoices',
            'user-see-all-outgoing-invoices-belonging-to-accessible-companies',
            'user-see-all-outgoing-invoices-belonging-to-own-company',
            'user-see-own-outgoing-invoices-and-involved'
        ].includes(role))
    }

    get canEditOutgoingInvoices() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-can-change-outgoing-invoices'))
    }

    get hasAccessToBankStatementTransactions() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-bank-statement-transactions'))
    }

    get hasAccessToLiquidityPlanning() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-liquidity-planning'))
    }

    get canExportInvoice() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-export-invoice'))
    }

    get exportModuleName() {
        return this.isDatevExportActive() ? this.$t('configuration.datev.description') :
            (this.isDatevCSVExportActive() ? this.$t('configuration.datev-csv.description') :
                this.$t('configuration.ms365.description'))
    }

    get canDeleteDocumentDirectly() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-can-delete-document-directly'))
    }

    get canInsertProduct() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-can-insert-product'))
    }

    get canInsertSupplierOnProduct() {
        return this.canInsertOrEditMasterData
    }

    get canInsertOrEditMasterData() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-can-insert-supplier'))
    }

    get canEditClients() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-clients'))
    }

    get canEditCostCenters() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-cost-centers'))
    }

    get canEditPostingKeys() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-posting-keys'))
    }

    get canEditDepartments() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-departments'))
    }

    get canEditSuppliers() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-suppliers'))
    }

    get canEditBusinessPartners() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-business-partners'))
    }

    get canEditProductCatalog() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-products'))
    }

    get canEditProductGroups() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.includes('masterdata-edit-product-groups'))
    }

    get hasAnyMasterDataPermission() {
        return this.canEditClients
            || this.canEditCostCenters
            || this.canEditPostingKeys
            || this.canEditDepartments
            || this.canEditSuppliers
            || this.canEditBusinessPartners
            || this.canEditProductCatalog
            || this.canEditProductGroups
    }

    get hasAccessToAllClientAddresses() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-see-client-addresses'))
    }

    get canEditContract() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-change-contract'))
    }

    get canInsertContract() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-insert-contract'))
    }

    get messageConfirmBeforeLeaveInvoiceForm() {
        return this.$t('invoice.page.confirm_before_leave_msg')
    }

    get messageConfirmBeforeLeaveContractForm() {
        return this.$t('contract.page.confirm_before_leave_msg')
    }

    get messageConfirmBeforeLeaveProcurementForm() {
        return this.$t("procurement.page.before_leave_msg")
    }

    get isChangingProcurementAllowed() {
        return this.isAdmin || (!!this.getCurrentUser()?.jwtUser?.roles?.find((role: any) => role == 'user-change-order'))
    }

    getUserInfo(callback: any, loggedUser: boolean = false) {
        let currentUser = loggedUser ? this.getLoggedUser() : this.getCurrentUser()
        if (currentUser) {
            return gqlQueryRaw((isAccessingFromOperatorArea()) ? this.getSimpleUserInfosGql : this.getUserGql,
                {id: currentUser.jwtUser.userId}
            ).then((result: any) => {
                if (!result?.data?.user) logout()
                else return callback(result.data.user)
            })
        } else return new Promise((resolve: any) => resolve(false));
    }

    getCurrencyFormat(
        value: number,
        locale: string = 'de-DE',
        showSymbol: boolean = true,
        currency: string = "EUR",
        displayPrecision?: number
    ) {
        const options: Intl.NumberFormatOptions = showSymbol ? {style: "currency", currency: currency ?? 'EUR'} : {}
        options.minimumFractionDigits = displayPrecision ?? 2
        options.maximumFractionDigits = displayPrecision ?? this.defaultDecimalPlaces

        try {
            const formatter = new Intl.NumberFormat(locale ?? 'de-DE', options)
            let formattedValue = formatter.format(value ?? 0)

            if (showSymbol && currency === 'USD') {
                const parts = formatter.formatToParts(value ?? 0)
                const symbol = parts.find(p => p.type === 'currency')?.value ?? '$'
                if (symbol === '$') {
                    formattedValue = formattedValue.replace('$', 'US$')
                }
            }
            return formattedValue
        } catch (e) {
            const number = new Intl.NumberFormat(locale ?? 'de-DE', {
                minimumFractionDigits: options.minimumFractionDigits,
                maximumFractionDigits: options.maximumFractionDigits
            }).format(value ?? 0)
            return showSymbol ? `${number} ${currency ?? 'EUR'}` : number
        }
    }

    getPercentFormat(value: number, locale: string = 'de-DE') {
        let percent = Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 2
        });
        return percent.format(value / 100)
    }

    getBCaseLabelByType(type?: string) {
        switch (type) {
            case "procurement":
                return this.$t('procurement_id')
            case "contract":
                return this.$t('contract_id')
            case "invoice":
                return this.$t('invoice_id')
            case "outgoing-invoice":
                return this.$t('outgoing_invoice_id')
            default:
                return ''
        }
    }

    getPostingKeyByTax(postingKeys: any, tax: any) {
        try {
            return postingKeys?.find((p: any) => p.tax == tax)
        } catch (e) {
            return null
        }
    }

    triggerHighlightElementAndFocus(element: string) {
        this.highlightElement(element)
        this.focusElement(`.highlight-element.${element}`)
    }

    highlightElement(element: string) {
        const el = (document.querySelector(`.highlight-element.${element}`) as HTMLElement)
        if (el) {
            el.classList.add('custom-highlight')
            setTimeout(() => {
                el.classList.remove('custom-highlight')
            }, 1500)
        }
    }

    focusElement(element: string) {
        const el = (document.querySelector(element)?.firstChild as HTMLElement)
        if (el) {
            el.scrollIntoView({behavior: "smooth", block: "center"})
        }
    }

    focusInput(selector: string) {
        const el = document.querySelector(`${selector} input`) as HTMLInputElement | null
        if (el) {
            el.focus()
        }
    }

    verifyAndSendNotificationOfIncomingTask(userFromId: number, userToId: any, timelineId: number, nextStatus: any = '') {
        const loggedUserId = this.getCurrentUser()?.jwtUser?.userId
        if (nextStatus != ForwardType.completed && userFromId != userToId && userToId != loggedUserId)
            this.notifyUserOfIncomingTask(timelineId)
    }

    notifyUserOfIncomingTask(timelineId?: number) {
        this.lbFetch({
            url: "/api/events/afterAccept?timelineId=" + timelineId,
            method: "POST",
            showError: false
        })
    }

    notifyUserTaskWasTaken(userFromId: number, userToId: any, timelineId: number) {
        if (userFromId != userToId) {
            const urlQuery = `userId=${userFromId}`
            const action = 'change-editor'
            this.lbFetch({
                url: `/api/events/${action}?timelineId=${timelineId}&${urlQuery}`,
                method: "POST",
                showError: false
            })
        }
    }

    notifyUsersOfTimelineComment(timelineId?: number, comment?: string) {
        const normalizedComment = comment?.trim()
        if (!timelineId || !normalizedComment) return

        this.lbFetch({
            url: `/api/events/timeline-comment?timelineId=${timelineId}`,
            method: "POST",
            data: {comment: normalizedComment},
            showError: false
        }).then(r => {
        })
    }

    setFocusOnFirstError(that: any, errors: any) {
        const field = _.keys(errors)[0];
        const fieldRef = that.$refs[field] as any;
        fieldRef?.$el?.parentNode?.parentNode?.scrollIntoView({block: 'center'});

        const refs = [fieldRef, fieldRef?.$refs?.[field], fieldRef?.$refs?.[field]?.$refs?.[field]];
        const validationFocusTarget = refs.find(ref => typeof ref?.focusForValidation === 'function');
        if (validationFocusTarget) {
            that.$nextTick(() => validationFocusTarget.focusForValidation());
            return;
        }

        const focusTarget = refs.find(ref => typeof ref?.focus === 'function');
        if (focusTarget) that.$nextTick(() => focusTarget.focus());
    }

    getFirstChars(text: string, count: number = 25) {
        return !!text && text.length > count ? text.substring(0, count - 5) + '...' : text
    }


    onModeViewChange(newValue: string, callbackFn?: any) {
        if (!Vue.hasUnsavedChanges || window.confirm(this.txtBeforeRouteLeave)) {
            Vue.hasUnsavedChanges = false
            this.viewMode = newValue
            if (newValue == 'list' && callbackFn) callbackFn()
            this.$router.push({query: {viewMode: newValue}})
        }
    }

    changeLanguage(language?: string) {
        if (!language) return
        const wasChanged = this.languageWasChanged(language)
        localStorage.setItem('lang', language)
        this.$i18n.setLocale(language)
        if (wasChanged) window.location.reload()
    }

    languageWasChanged(language: string) {
        return localStorage.getItem('lang') != language
    }

    get currentLanguage() {
        return localStorage.getItem('lang') || 'de'
    }

    getTranslationForCurrentLanguageOrFallback(item: any) {
        return this.getTranslation(item) ?? this.$t('translation-component.no_translation')
    }

    getTranslation(item: any, listName: string = 'translations', fieldName: string = 'translation', defaultLanguage?: string) {
        if (!item) return ''

        const list = isArray(item[listName]) ? item[listName]?.filter((it: any) => it[fieldName] != "") :
            (isArray(item[listName]?.data) ? item[listName].data?.filter((it: any) => it[fieldName] != "") :
                [])

        if (defaultLanguage) {
            const translation = list?.find((item: any) => item.language === defaultLanguage)
            if (translation && translation[fieldName]) return translation[fieldName]
        }

        const hasMoreTranslations = list?.length > 0
        const translation = hasMoreTranslations ?
            list.find((item: any) => item.language === this.currentLanguage) ||
            list.find((item: any) => item.language === this.tenantLanguage) ||
            list[0] : null

        return translation ? translation[fieldName] : item[fieldName] ?? item?.name
    }

    getTranslationArray(item: any, listName: string = 'translations', fieldName: string = 'translation') {
        return item[listName]?.map((translation: any) => translation[fieldName]) ?? []
    }

    hasTranslationWithValue(item: any, searchedValue: string, listName: string = 'translations', fieldName: string = 'translation') {
        return this.getTranslationArray(item, listName, fieldName).includes(searchedValue)
    }

    getTranslatedJExcelList(list: any, language?: string, listName: string = 'translations', fieldName: string = 'translation') {
        return list.map((item: any) => {
            return {
                id: item.id,
                name: this.getTranslation(item, listName, fieldName, language)
            }
        })
    }

    sortTranslation(rowA: any, rowB: any) {
        return this.sort(
            this.getTranslationForCurrentLanguageOrFallback(rowA)?.toLowerCase(),
            this.getTranslationForCurrentLanguageOrFallback(rowB)?.toLowerCase()
        )
    }


    sortedProp: any = null
    sortedColumnId: any = null
    order: any = null

    sortChange(column: any) {
        if (this.sortedColumnId) {
            const elColOld = (document.querySelector(`th.${this.sortedColumnId}`)) as HTMLElement
            if (elColOld) elColOld.classList.remove(this.order)
        }

        this.sortedColumnId = column.column.id
        this.sortedProp = column.prop
        this.order = column.order

        setTimeout((this.$refs.list as any)?.clearSort(), 1000)

        const elColNew = (document.querySelector(`th.${this.sortedColumnId}`)) as HTMLElement
        elColNew.classList.add(this.order)
    }


    sort(a: any, b: any) {
        const tempA = typeof a === 'string' ? a.trim().toLowerCase() : a
        const tempB = typeof b === 'string' ? b.trim().toLowerCase() : b
        if (tempA < tempB) return -1
        if (tempA > tempB) return 1
        return 0
    }

    sortProp(a: any, b: any, prop: string) {
        return this.sort(a[prop], b[prop])
    }


    /**
     * START: Functions for JExcel
     * */

    generateSpreadsheet(spreadsheetInstance: any, data: any, columns: any, nestedHeaders?: any, freezeColumns?: any, tableWidth?: string) {
        this.jExcelColumns = columns
        this.jExcelOptions = {
            data: data?.length > 0 ? data : [[0]],
            columns,
            nestedHeaders,
            freezeColumns,
            onchange: this.onCellChanged,
            text: this.$t('jexcel'),
            allowInsertColumn: false,
            allowManualInsertColumn: false,
            allowDeleteColumn: false,
            allowComments: false,
            allowRenameColumn: false,
            defaultColAlign: 'left',
            tableHeight: '600px',
            tableWidth,
            tableOverflow: true,
            csvHeaders: true,
            includeHeadersOnDownload: true
        };
        this.jExcelObj = jexcel(spreadsheetInstance, this.jExcelOptions);
    }

    clearJExcelElement() {
        const table = document.getElementById("spreadsheet")
        if (!!table) table.innerHTML = ""
    }

    getJexcelCellName(columnIndex: number, rowIndex: number) {
        return jexcel.getColumnNameFromId([columnIndex, rowIndex]);
    }

    setJexcelValueByIndex(columnIndex: number, rowIndex: number, value: any) {
        const cellName = this.getJexcelCellName(columnIndex, rowIndex)
        this.jExcelObj.setValue(cellName, value)
    }

    onCellChanged(instance: any, cell: any, x: any, y: any, value: any) {
        let cellName = jexcel.getColumnNameFromId([x, y]);
        if (!this.validateCell(x, value)) this.jExcelObj.setStyle(cellName, 'background-color', '#fbc4c4');
        else this.jExcelObj.setStyle(cellName, 'background-color', 'white');
        Vue.hasUnsavedChanges = true;
    }

    invalidateCell(columnIndex: any, rowIndex: any) {
        let cellName = jexcel.getColumnNameFromId([columnIndex, rowIndex]);
        this.jExcelObj.setStyle(cellName, 'background-color', '#fbc4c4');
    }

    showInvalidCells() {
        this.jExcelValidationErrors.map((error: any) => {
            error.columns.map((column: any) => {
                this.invalidateCell(column.id, error.row)
            })
        })

        const hasRequiredColumn = this.jExcelValidationErrors.find((error: any) => error.type == 'required');
        const hasUniqueColumn = this.jExcelValidationErrors.find((error: any) => error.type == 'unique');

        if (hasRequiredColumn)
            this.$message({
                message: "Bitte geben Sie die fehlenden Pflichtfelder ein.",
                type: "error"
            })

        if (hasUniqueColumn)
            this.$message({
                message: "Die " + hasUniqueColumn.columns[0].title + " muss eindeutig sein.",
                type: "error"
            })
    }

    validateCell(columnIndex: number, value: any) {
        let isRequired = this.jExcelColumns.find((column: any) => column.id && column.id == columnIndex)?.required
        return (!!isRequired && !!value) || !isRequired
    }

    searchJExcel(search: string) {
        if (!this.jExcelObj?.search) return
        this.jExcelObj.search(search)
    }

    updateSearch(search: any, searchRegEx: any) {
        this.search = search
        this.searchRegEx = searchRegEx
        this.searchJExcel(search)
    }

    getAndShowValidationErrorsOnJExcelTable() {
        if (!this.jExcelObj?.getJson) return []
        let dataTable = this.jExcelObj.getJson()
        const errors = getValidationErrorsFromDataTable(dataTable, this.jExcelColumns)
        errors.map((error: any) => {
            error.columns.map((column: any) => {
                this.invalidateCell(column.id, error.row)
            })
        })
        return errors
    }

    saveAndRefreshJExcel(
        upsertMutation: any,
        listObject: any,
        config?: { table: string, fields: string[], lists?: any },
        callbackFnSuccess?: any,
        callbackFnError?: any,
        language?: string
    ) {
        if (!this.jExcelObj?.getJson) {
            this.jExcelValidationErrors = []
            return
        }
        let dataTable = this.jExcelObj.getJson()
        const isQueryObject = !!listObject.result

        this.jExcelValidationErrors = getValidationErrorsFromDataTable(dataTable, this.jExcelColumns)
        if (!this.jExcelValidationErrors.length) {
            let data = {
                objects: formatDataTableBeforeSave(dataTable, this.jExcelColumns, config?.table, language),
                idsToDelete: getRemovedIdsFromDataTable(isQueryObject ? listObject.result?.list : listObject, dataTable)
            } as any

            const relations = getRelationsToSave(dataTable, this.jExcelColumns, config?.table, language)
            Object.keys(relations)?.forEach((relationIndex: any) => {
                data[relationIndex] = _.uniqWith(relations[relationIndex], _.isEqual) ?? []
            })

            this.lbMutateWithCustomHeader(upsertMutation, data, {'x-hasura-app-source': 'jexcel'})
                .then(() => {
                    Vue.hasUnsavedChanges = false

                    if (isQueryObject) {
                        const oldList = listObject.result?.list
                        listObject.refetch()?.then(() => {
                            const newList = listObject.result?.list
                            if (!!config?.table) this.addAuditLogsForChanges(config, oldList, newList)
                            if (!!callbackFnSuccess) callbackFnSuccess()
                        })
                    } else {
                        if (!!callbackFnSuccess) callbackFnSuccess()
                    }
                })
                .catch(() => {
                    if (!!callbackFnError) callbackFnError()
                })
        } else {
            this.showInvalidCells()
            if (!!callbackFnError) callbackFnError()
        }
    }

    addAuditLogsForChanges(auditLogConfig: {
        table: string,
        fields: string[],
        lists?: any
    }, oldList: any, newList: any) {
        const oldItemsIds = oldList.map((oldItem: any) => {

            let keptItem = newList.find((newItem: any) => newItem.id == oldItem.id)
            if (keptItem) {
                const createdAt = dayjs().toString()

                auditLogService.saveAuditLogItem(
                    auditLogConfig.table,
                    AuditLogType.update,
                    oldItem.id,
                    _.pick(oldItem, auditLogConfig.fields),
                    _.pick(keptItem, auditLogConfig.fields),
                    undefined, undefined,
                    createdAt
                )

                if (!!auditLogConfig.lists) {
                    _.keys(auditLogConfig.lists).forEach((relationTable: string) => {
                        auditLogService.saveAuditLogListByKeys(
                            auditLogConfig.table,
                            oldItem.id,
                            oldItem[auditLogConfig.lists[relationTable]],
                            keptItem[auditLogConfig.lists[relationTable]],
                            relationTable,
                            undefined,
                            createdAt)
                    })
                }

            } else auditLogService.saveAuditLogItem(
                auditLogConfig.table,
                AuditLogType.delete,
                oldItem.id,
                oldItem,
                {}
            )

            return oldItem.id
        })

        newList.filter((newItem: any) => !oldItemsIds.includes(newItem.id)).forEach((newItem: any) => {
            auditLogService.saveAuditLogItem(
                auditLogConfig.table,
                AuditLogType.insert,
                newItem.id,
                {},
                _.pick(newItem, auditLogConfig.fields)
            )
        })
    }


    findLists() {
        let findListsGql = gql`
            query findLists{
                departments:department(where: {active: {_eq: true}}) {
                    id
                    email
                    name:translated_name
                }
                clients:client(where: {active: {_eq: true}}) {
                    id
                    email
                    name
                    client_number
                }
                users:lb_user(where: {active: {_eq: true}}) {
                    id
                    email
                    name
                }
                suppliers:supplier(where: {active: {_eq: true}}) {
                    id
                    email
                    name
                }
                product_groups:product_group(where: {active: {_eq: true}}) {
                    id
                    name:translated_name
                }
                cost_centers:cost_center(where: {active: {_eq: true}}) {
                    id
                    name:translated_name
                    number
                }
                permissions:permission(where: {active: {_eq: true}}) {
                    id
                    name
                }
                task_groups:lb_user(where: {active: {_eq: true}, is_group: {_eq: true}}) {
                    id
                    name:translated_name
                }
            }
        `
        return gqlQueryRaw(findListsGql).then((result: any) => {
            return result.data
        })
    }

    beforeWindowUnload(e: any) {
        if (Vue.hasUnsavedChanges && !window.confirm(this.txtBeforeRouteLeave)) {
            // Cancel the event
            e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
            // Chrome requires returnValue to be set
            e.returnValue = '';
        }
    }

    formatFeedbackMsg(data: any) {
        let msg = this.$t(`system_comment.${data.type}`)
        _.keys(data?.values)?.forEach((key: string) => {
            msg = msg.replace(`{${key}}`, data?.values[key])
        })
        return msg.replace(/\{[^}]+\}/g, '')
    }

    formatErrorMsgFromExport(feedback: any) {
        return this.$t('invoice.page.export_error') +
            (feedback.data?.type != 'on_ms365_export_error_generic' ?
                this.formatFeedbackMsg(feedback.data) : feedback.message)
    }

    /**
     * END: Functions for JExcel
     * */

    markFormChange() {
        Vue.hasUnsavedChanges = true
    }

    getIntValue(value: any) {
        const onlyNumbers = value?.toString().replace(/\D/g, '')
        return onlyNumbers != "" ? parseInt(onlyNumbers) : null
    }

    /**
     * Audit-Logs
     */

    getAffectedProperties(auditLog: AuditLog) {
        return auditLog.type == AuditLogType.update ? auditLog.affected_properties :
            (auditLog.type == AuditLogType.insert ?
                    this.getKeysOfAffectedProperties(auditLog.data_after, auditLog.relation_table) :
                    this.getKeysOfAffectedProperties(auditLog.data_before, auditLog.relation_table)
            )
    }

    getKeysOfAffectedProperties(data: any, relationTable?: string) {
        if (this.$t(relationTable + '.label_log') != "") return []
        const fieldsToIgnore = ["error", "title", "__typename", 'active', 'id']
        return _.keys(data)?.filter((key: string) =>
            typeof data[key] != "object"
            && !fieldsToIgnore.includes(key)
            && (!relationTable || !key.endsWith('_id'))
        )
    }

    getTranslatedLabel(auditLog: AuditLog, property: string) {
        return (!!auditLog.relation_table ?
                this.$t(auditLog.relation_table + '.' + property) :
                this.$t(auditLog.table + '.' + property)) ||
            this.$t(property) ||
            property
    }

    getHtmlTitleForRelationTable(auditLog: AuditLog) {
        const name = this.getNameFromAuditLogObject(auditLog)

        if (!!auditLog.relation_table && (!!name || auditLog.type == AuditLogType.info)) {
            const description = this.getDescriptionForRelationTable(auditLog.relation_table)
            const icon = this.getIconForAuditLogByType(auditLog)
            if (description) {
                if (auditLog.type == AuditLogType.info) return `<b>${description}:</b><br>`
                else return `<b>${description}:</b><br><div style="display: flex">${icon}&nbsp; ${name}</div><br>`
            }
            return `<div style="display: flex">${icon}&nbsp; ${name}</div><br>`
        }

        return ""
    }

    getHtmlTitleForCustomField(auditLog: AuditLog) {
        const name = this.getNameFromAuditLogObject(auditLog)
        const icon = this.getIconForAuditLogByType(auditLog)
        return `<div style="display: flex">${icon}&nbsp; ${name}</div>`
    }

    getDescriptionForRelationTable(relationTable?: string) {
        return this.$t(relationTable + '.description')
    }

    getNameFromAuditLogObject(auditLog: AuditLog) {
        return this.getName(auditLog.data_before, auditLog.relation_table) ?? this.getName(auditLog.data_after, auditLog.relation_table)
    }

    getName(data: any, relationTable?: string) {
        if (!data) return null
        const template = omitWarning(() => {
            return this.$t(relationTable + '.label_log')
        })
        let templateFields = omitWarning(() => {
            return this.$t(relationTable + '.label_log_fields')
        })
        let table = omitWarning(() => {
            return this.$t(relationTable + '.table_log')
        })

        templateFields = templateFields != "" ? templateFields.split(',') : null
        const label = this.formatLabelFromTemplate(template, data, templateFields)
        table = table != "" ? table : relationTable
        return label || (!!table && !!data[table]?.name ? data[table].name : null)
    }

    formatLabelFromTemplate(template: string, data: any, templateFields?: any) {
        const label = template + ''
        if (template != "") {
            (templateFields ?? _.keys(data)).forEach((key: string) => {
                if (typeof data[key] != 'object' || data[key] == null)
                    template = template.replace(key, this.getTextAndFormatIfDate(data[key]))
            })
            return label != template ? template : null
        }
        return this.getTextAndFormatIfDate(data['name'])
    }

    getTextAndFormatIfDate(str: string) {
        return checkIfStringIsValidISODate(str) ? this.lbDate(str) :
            (this.isStringBoolean(str) ? this.getBooleanText(str) : (str ?? ''))
    }

    isStringBoolean(str: any) {
        return ['true', true, 'false', false].includes(str) || typeof str == 'boolean'
    }

    getBooleanText(str: any) {
        return ["true", true].includes(str) ? 'Ja' : 'Nein'
    }

    formatValueFromTemplate(str: string, property?: string, auditLog?: AuditLog) {
        const table = auditLog?.relation_table ?? auditLog?.table
        const type = omitWarning(() => {
            return this.$t(table + '.type_' + property)
        })
        if (type == 'float') str = parseFloat(str).toFixed(4).replace(".", ",")
        else if (type == 'currency') {
            const currencyCode = this.getCurrencyCodeFromAuditLog(auditLog)
            str = this.getCurrencyFormat(parseFloat(str), undefined, true, currencyCode)
        }
        const template = omitWarning(() => {
            return this.$t(table + '.template_' + property)
        }).replace('$1', str)
        const text = template != "" ? template : str
        return this.getTextAndFormatIfDate(text)
    }

    getCurrencyCodeFromAuditLog(auditLog?: AuditLog) {
        const currencyAfter = auditLog?.data_after ? auditLog?.data_after["currency"] : null
        const currencyBefore = auditLog?.data_before ? auditLog?.data_before["currency"] : null
        return currencyAfter || currencyBefore || "EUR"
    }

    getLocalizedTextDiffs(text1: any, text2: any, showInserts: boolean = true) {
        const dmp = new diff_match_patch()

        const removeHtmlTags = (text: string): string => text.replace(/<[^>]*>/g, '')

        let texts1: any, texts2: any
        try {
            texts1 = JSON.parse(text1)
            texts2 = JSON.parse(text2)
        } catch (error) {
            console.error("Error converting text to JSON:", error)
            texts1 = text1
            texts2 = text2
        }

        let result: string = ''

        Object.keys(texts1).forEach((lang: string) => {
            const originalText: string = removeHtmlTags(texts1[lang])
            const newText: string = removeHtmlTags(texts2[lang])

            const diffs = dmp.diff_main(originalText, newText)
            dmp.diff_cleanupSemantic(diffs)

            let modifiedText: string = ''//texts1[lang]
            let hasChanges: boolean = false

            diffs.forEach((diff: [number, string]) => {
                const [op, data] = diff
                const cleanData: string = data.replace(/</g, "&lt").replace(/>/g, "&gt")

                if (op === 1 && showInserts) {
                    hasChanges = true
                    modifiedText += `<span style="color: green">${cleanData}</span>`
                } else if (op === -1) {
                    hasChanges = true
                    modifiedText += `<span style="color: red"><s>${cleanData}</s></span>`
                } else {
                    modifiedText += cleanData;
                }
            })

            if (hasChanges) {
                const langLabel = this.languages?.find((l: any) => l.value === lang)?.label || lang.toUpperCase()
                result += `${langLabel}: „${modifiedText}“\n\n`
            }
        })

        return result.trim() || 'No differences found.'
    }

    getAuditLogForProperty(auditLog: AuditLog, property: string) {
        const isForeignKey = property.endsWith("_id")
        const isInsertType = auditLog.type == AuditLogType.insert
        const propertyName = isForeignKey ? property.replace("_id", "_name") : property
        const propertyTranslationName = property.replace("_id", "") + "_names"
        const hasTranslationBefore = !!auditLog.data_before && auditLog.data_before[propertyTranslationName]?.length > 0
        const hasTranslationAfter = !!auditLog.data_after && auditLog.data_after[propertyTranslationName]?.length > 0
        const hasDataBefore = !!auditLog.data_before && (hasTranslationBefore || this.propertyHasValue(auditLog.data_before[property]))
        const hasDataAfter = !!auditLog.data_after && (hasTranslationAfter || this.propertyHasValue(auditLog.data_after[property]))

        let content: any = ''
        const type = omitWarning(() => {
            return this.$t(auditLog?.table + '.type_' + property)
        })

        if (type == 'html-i18n') {
            content = `<b>${this.getTranslatedLabel(auditLog, property)}:</b><br>
                        <i>${this.getLocalizedTextDiffs(auditLog.data_before[property], auditLog.data_after[property])}</i>`
        } else {

            const dataBefore = hasDataBefore ?
                (hasTranslationBefore ?
                        this.getTranslation(auditLog.data_before, propertyTranslationName) :
                        this.formatValueFromTemplate(auditLog.data_before[propertyName] ?? auditLog.data_before[property], property, auditLog)
                ) :
                (isInsertType ? '' : this.$t('audit_log.none'))

            const dataAfter = hasDataAfter ?
                (hasTranslationAfter ?
                    this.getTranslation(auditLog.data_after, propertyTranslationName) :
                    this.formatValueFromTemplate(auditLog.data_after[propertyName] ?? auditLog.data_after[property], property, auditLog)) :
                this.$t('audit_log.none')

            const txtDataBefore = isInsertType ? '' : `<br><i>${dataBefore}</i> <b>&nbsp; →  &nbsp; </b>`
            content = hasDataBefore || hasDataAfter ?
                `<b>${this.getTranslatedLabel(auditLog, property)}:</b>${txtDataBefore} <i>${dataAfter}</i>` : false
            if (!!content && auditLog.relation_table) content = `<div style="margin-left:20px">${content}</div>`

        }
        return content
    }

    getHtmlTextFromAuditLogForCustomField(auditLog: AuditLog) {
        const property = 'value'
        const hasTranslationBefore = !!auditLog.data_before && auditLog.data_before[property]?.length > 0
        const hasTranslationAfter = !!auditLog.data_after && auditLog.data_after[property]?.length > 0
        const hasDataBefore = !!auditLog.data_before && (hasTranslationBefore || this.propertyHasValue(auditLog.data_before[property]))
        const hasDataAfter = !!auditLog.data_after && (hasTranslationAfter || this.propertyHasValue(auditLog.data_after[property]))

        const dataBefore = hasDataBefore ?
            this.formatValueFromTemplate(auditLog.data_before[property] ?? auditLog.data_before[property], property, auditLog)
            : this.$t('audit_log.none')

        const dataAfter = hasDataAfter ?
            this.formatValueFromTemplate(auditLog.data_after[property] ?? auditLog.data_after[property], property, auditLog)
            : this.$t('audit_log.none')

        const content = `<i>${dataBefore}</i> <b>&nbsp; →  &nbsp; </b>  <i>${dataAfter}</i>`
        return `<div>${content}</div>`
    }

    propertyHasValue(value: any) {
        return (!!value && value != "") || (value === false || value === 'false' || value === 0)
    }


    getAuditLogForCheckbox(auditLog: AuditLog) {
        const differences = _.xor(auditLog.data_before, auditLog.data_after)
        const defaultParams = {export_name: this.exportModuleName}
        return differences.map((item: any) => {
            return this.$t(`${auditLog.table}.${auditLog.relation_table}.${item}`, defaultParams)
                + ": <br><i>" +
                (auditLog.data_before.includes(item) ? this.$t('yes') : this.$t('no')) +
                "</i> <b>&nbsp; →  &nbsp; </b><i>" +
                (auditLog.data_after.includes(item) ? this.$t('yes') : this.$t('no')) + "</i>"
        }).join("<br><br>")
    }

    getAuditLogTypeLabel(auditLogs: AuditLog[]) {
        const type = auditLogs.length == 1 ? auditLogs[0].type : AuditLogType.update
        return this.$t('audit_log.type.' + type)
    }

    getFormattedHtmlTextFromAuditLog(auditLog: AuditLog, relationTable?: string, length?: number) {
        if (auditLog.type === AuditLogType.checkbox) return this.getAuditLogForCheckbox(auditLog)

        const htmlText: string = this.getHtmlTitleForRelationTable(auditLog)
        const historicalOnboardingSource = auditLog.type === AuditLogType.insert
            ? auditLog.data_after?.creation_source
            : null
        const createdByHistoricalOnboarding = historicalOnboardingSource === 'historical_invoice_onboarding' || historicalOnboardingSource === 'historical_procurement_onboarding'
            ? `<br><i>${this.$t(`audit_log.msg.created_by_${historicalOnboardingSource}`)}</i>`
            : ''

        if (auditLog.type === AuditLogType.info && auditLog.info) {
            const info = (
                    auditLog.relation_table ?
                        this.$t(auditLog.relation_table + '.' + auditLog.info, auditLog.data_after) :
                        this.$t(auditLog.info, auditLog.data_after)
                )
                || auditLog.info
            return `${htmlText} <br><div style="margin-bottom: 5px">${info}</div>${createdByHistoricalOnboarding}`
        }

        if (this.shouldShowAffectedProperties(auditLog, relationTable)) {
            const affectedProperties = this.getAffectedProperties(auditLog)
            if (affectedProperties?.length > 0) {
                const propsInFormSequence = this.getPropsInFormSequence(auditLog, relationTable)?.filter((prop: string) => affectedProperties?.includes(prop))
                const props = propsInFormSequence?.length > 0 ? propsInFormSequence : affectedProperties
                return htmlText + props.map((property: string) => this.getAuditLogForProperty(auditLog, property))
                    ?.filter((it: any) => !!it)
                    .map((text: any) => length ? this.getFirstChars(text, length) : text)
                    .map((text: any) => `<div style="margin-bottom: 5px">${text}</div>`)
                    .join('') + createdByHistoricalOnboarding
            }
            return htmlText + createdByHistoricalOnboarding
        } else if (!relationTable) {
            if (auditLog.type === AuditLogType.delete) return this.$t('audit_log.msg.was_deleted')
            else if (auditLog.type === AuditLogType.insert) return this.$t('audit_log.msg.was_inserted') + createdByHistoricalOnboarding
        } else return htmlText + createdByHistoricalOnboarding
    }

    getPropsInFormSequence(auditLog: AuditLog, relationTable?: string) {
        const table = relationTable && relationTable == auditLog.relation_table ? relationTable : auditLog.table
        const props = this.$t(`${table}.form_sequence`).split(',')
        return props.map((prop: string) => prop.trim())
    }

    mustShowGroupedRelationTable(auditLogs: AuditLog[]) {
        return !!auditLogs[0].relation_table &&
            !this.shouldShowAffectedProperties(auditLogs[0], auditLogs[0].relation_table) &&
            !this.isAuditLogTypeCheckbox(auditLogs[0]) &&
            !this.isAuditLogTypeInfo(auditLogs[0])
    }

    isAuditLogTypeCheckbox(auditLog: AuditLog) {
        return auditLog.type === AuditLogType.checkbox
    }

    isAuditLogTypeInfo(auditLog: AuditLog) {
        return auditLog.type === AuditLogType.info
    }

    getFormattedHtmlTextFromAuditLogsForRelationTable(auditLogs: AuditLog[]) {
        const htmlText: string = `<b>${this.getDescriptionForRelationTable(auditLogs[0].relation_table)}</b>`
        return htmlText + this.getAuditLogsSortedByType(auditLogs).map((auditLog: AuditLog) => {
            const icon = this.getIconForAuditLogByType(auditLog)
            return icon + this.getNameFromAuditLogObject(auditLog)
        }).map((text: any) => `<div style="display:flex;margin-bottom: 2px;align-items: center">${text}</div>`)
            .join('') + "<br>"
    }

    iconSquarePen = "<svg xmlns=\"http://www.w3.org/2000/svg\" height=\"1em\" viewBox=\"0 0 448 512\"><path fill='currentColor' d=\"M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zM325.8 139.7l14.4 14.4c15.6 15.6 15.6 40.9 0 56.6l-21.4 21.4-71-71 21.4-21.4c15.6-15.6 40.9-15.6 56.6 0zM119.9 289L225.1 183.8l71 71L190.9 359.9c-4.1 4.1-9.2 7-14.9 8.4l-60.1 15c-5.5 1.4-11.2-.2-15.2-4.2s-5.6-9.7-4.2-15.2l15-60.1c1.4-5.6 4.3-10.8 8.4-14.9z\"/></svg>"
    iconSquarePlus = "<svg xmlns=\"http://www.w3.org/2000/svg\" height=\"1em\" viewBox=\"0 0 448 512\"><path fill='currentColor'  d=\"M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zM200 344V280H136c-13.3 0-24-10.7-24-24s10.7-24 24-24h64V168c0-13.3 10.7-24 24-24s24 10.7 24 24v64h64c13.3 0 24 10.7 24 24s-10.7 24-24 24H248v64c0 13.3-10.7 24-24 24s-24-10.7-24-24z\"/></svg>"
    iconSquareMinus = "<svg xmlns=\"http://www.w3.org/2000/svg\" height=\"1em\" viewBox=\"0 0 448 512\"><path fill='currentColor'  d=\"M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm88 200H296c13.3 0 24 10.7 24 24s-10.7 24-24 24H152c-13.3 0-24-10.7-24-24s10.7-24 24-24z\"/></svg>"

    getIconForAuditLogByType(auditLog: AuditLog) {
        switch (auditLog.type) {
            case AuditLogType.insert:
                return `<div class="icon-audit-log-type insert">${this.iconSquarePlus}</div>`
            case AuditLogType.update:
                return `<div class="icon-audit-log-type update">${this.iconSquarePen}</div>`
            case AuditLogType.delete:
                return `<div class="icon-audit-log-type delete">${this.iconSquareMinus}</div>`
        }
        return ""
    }


    getAuditLogsSortedByType(auditLogs: AuditLog[]) {
        return _.sortBy(auditLogs, "type")
    }

    getAuditLogsGroupedByRelationTable(auditLogs: AuditLog[]) {
        const groups = _.groupBy(auditLogs, "relation_table")
        return sortDictionaryOnKeys(groups)
    }


    shouldShowAffectedProperties(auditLog: AuditLog, relationTable?: string) {
        const hasMoreFkeys = _.keys(auditLog.data_after || auditLog.data_before)?.filter((key: string) => key.endsWith('_id'))?.length > 1
        return auditLog.type === AuditLogType.update ||
            (!!relationTable && auditLog.type === AuditLogType.insert && !!auditLog.data_after[auditLog.table + '_id'] && !hasMoreFkeys)
    }

    //PUNCH-IN
    sendPunchInStatusUpdate(code: string, documentId: number) {
        this.lbFetch({
            url: `/api/punchin/status-update/${code}/document/${documentId}`,
            showError: false
        }).then(r => {
        })
    }


    isLoggedAsOtherUser() {
        return !!localStorage.getItem(LSK_ACCESS_JWT_OTHER) || !!localStorage.getItem(LSK_REFRESH_JWT_OTHER)
    }

    logoutAsOtherUser() {
        clearFrontendConfigCache()
        localStorage.removeItem(LSK_ACCESS_JWT_OTHER)
        localStorage.removeItem(LSK_REFRESH_JWT_OTHER)
        this.emitter.emit("reload-navbar")
        this.$router.push({name: 'list-user'})
    }

    areAllModulesActive() {
        return this.isInvoiceModuleActive() && this.isProcurementModuleActive()
    }

    isOnlyInvoiceModuleActive() {
        return this.isInvoiceModuleActive() && !this.isProcurementModuleActive()
    }

    isOnlyProcurementModuleActive() {
        return this.isProcurementModuleActive() && !this.isInvoiceModuleActive()
    }

    isInvoiceModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.invoice')
        return !config  //new tenants have no config saved yet and the default is true
            || config.value == 'true'
    }

    isOutgoingInvoiceModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.outgoing-invoice')
        return config?.value == 'true'
    }

    isBankStatementModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.bank-statements')
        return config?.value == 'true'
    }

    isContractModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.contract')
        return config?.value == 'true'
    }

    isProcurementModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.procurement')
        return !config  //new tenants have no config saved yet and the default is true
            || config.value == 'true'
    }

    isPermissionsModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.permissions')
        return config?.value == 'true'
    }

    isMultilingualismModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.multilingualism')
        return config?.value == 'true'
    }

    isSupplierCommunicationModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.supplier-communication')
        return config?.value == 'true'
    }

    isLiquidityPlanningModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.liquidity-planning')
        return config?.value == 'true'
    }

    isDatevExportActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.invoice.export')
        return !config || config?.value == 'datev'
    }

    isDatevCSVExportActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.invoice.export')
        return config?.value == 'datev-csv'
    }

    isMS365ExportActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.modules.invoice.export')
        return config?.value == 'ms365'
    }

    shouldInvoiceHaveApprovalStep(hasOrder: boolean = false) {
        const config = this.configurations?.find((item: any) => item.key == 'invoice.form.approval')
        if (!config || config.value == 'never') return false
        else if (config.value == 'always') return true
        else return config?.value == 'if-no-order' && !hasOrder
    }

    shouldSaveLinesFromInvoiceRecognition() {
        const config = this.configurations?.find((item: any) => item.key == 'invoice.recognition.lines')
        return !config || config?.value == 'true'
    }

    shouldGroupInvoiceRecognitionLinesByTax() {
        const config = this.configurations?.find((item: any) => item.key == 'invoice.recognition.group_by_tax')
        return config?.value == 'true'
    }

    isProcurementOrContractRequiredOnInvoiceForm() {
        const config = this.configurations?.find((item: any) => item.key == 'invoice.form.procurement-required')
        return config?.value == 'true'
    }

    isInsolvencyLawModulActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.defaults.show-insolvency-law-fields')
        return config?.value == 'true'
    }

    shouldConsiderApprovalLimitPriority() {
        const config = this.configurations?.find((item: any) => item.key == 'system.defaults.approval-limit-priority')
        return config?.value == 'true'
    }

    isCustomFieldsModuleActive() {
        const config = this.configurations?.find((item: any) => item.key == 'system.defaults.custom-fields')
        return config?.value == 'true'
    }

    getProcurementStatusLabelByBcase(bcase: any, currentEntry?: any) {
        const procurement = bcase.procurement ?? bcase
        currentEntry = currentEntry || bcase.timeline?.current_entry[0]
        return omitWarning(() => {
                return this.$t('procurement.statuses.' + procurement?.status)
            })
            || currentEntry?.name
            || omitWarning(() => {
                return this.$t('timeline.text.' + currentEntry?.type + '.name', currentEntry?.name_params)
            })
    }

    getProcurementStatusLabelByStatus(status: string) {
        return omitWarning(() => {
            return this.$t('procurement.statuses.' + status)
        })
    }

    findProcurementStatusByLabel(label: string) {
        const statuses = this.getAllProcurementStatuses()
        const keys = _.keys(statuses)

        return keys.find(key => (statuses[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (statuses[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    getAllProcurementStatuses() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.procurement?.statuses
    }

    getAllTimelineEntryTypes() {
        const texts = this.$i18n.messages[this.$i18n.getLocale()]?.timeline?.text
        let types: any = {}
        _.keys(texts)?.filter(type => !!texts[type].is_searchable)?.forEach(type => {
            types[type] = texts[type].simple_name || texts[type].name
        })
        return types
    }

    findTimelineEntryTypeByLabel(label: string) {
        const types = this.getAllTimelineEntryTypes()
        return _.keys(types).find(key => (types[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }


    getInvoiceStatusLabel(bcase: any, currentEntry?: any) {
        const invoice = bcase.invoice ?? bcase
        const bcaseType = bcase?.type ?? bcase?.bcase?.type
        currentEntry = currentEntry || bcase.timeline?.current_entry[0]
        if (bcaseType === 'outgoing-invoice') {
            return this.getAllOutgoingInvoiceStatuses()?.[invoice?.status]
                || currentEntry?.name
                || omitWarning(() => {
                    return this.$t('timeline.text.' + currentEntry?.type + '.name', currentEntry?.name_params)
                })
                || omitWarning(() => {
                    return this.$t('invoice.statuses.' + invoice?.status)
                })
        }
        return omitWarning(() => {
                return this.$t('invoice.statuses.' + invoice?.status)
            })
            || currentEntry?.name
            || omitWarning(() => {
                return this.$t('timeline.text.' + currentEntry?.type + '.name', currentEntry?.name_params)
            })

    }

    getInvoiceStatusLabelByStatus(status: string, type: string = 'invoice') {
        if (type === 'outgoing-invoice') {
            return this.getAllOutgoingInvoiceStatuses()?.[status]
                || omitWarning(() => {
                    return this.$t('invoice.statuses.' + status)
                })
        }
        return omitWarning(() => {
            return this.$t('invoice.statuses.' + status)
        })
    }

    getInvoiceStatusFilter(status: string) {
        return {status: {_eq: status}}
    }

    findInvoiceStatusByLabel(label: string) {
        const statuses = this.getAllInvoiceStatuses()
        const keys = _.keys(statuses)

        return keys.find(key => (statuses[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (statuses[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    getAllInvoiceStatuses() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.invoice?.statuses ?? {}
    }

    findOutgoingInvoiceStatusByLabel(label: string) {
        const statuses = this.getAllOutgoingInvoiceStatuses()
        const keys = _.keys(statuses)

        return keys.find(key => (statuses[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (statuses[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    getAllOutgoingInvoiceStatuses() {
        return {
            [ForwardType.open]: omitWarning(() => this.$t('invoice.statuses.open')),
            [ForwardType.approval]: omitWarning(() => this.$t('invoice.statuses.approval')),
            [ForwardType.accounting]: omitWarning(() => this.$t('invoice.statuses.accounting')),
            [ForwardType.completed]: omitWarning(() => this.$t('timeline.text.invoice-verified.name'))
                || omitWarning(() => this.$t('invoice.statuses.completed')),
            [ForwardType.declined]: omitWarning(() => this.$t('invoice.statuses.declined')),
        } as Record<string, string>
    }

    getContractStatusLabel(bcase: any, currentEntry?: any) {
        const contract = bcase.contract ?? bcase
        currentEntry = currentEntry || bcase.timeline?.current_entry[0]
        return omitWarning(() => {
                return this.$t('contract.statuses.' + contract?.status)
            })
            || currentEntry?.name
            || omitWarning(() => {
                return this.$t('timeline.text.' + currentEntry?.type + '.name', currentEntry?.name_params)
            })

    }

    getContractStatusLabelByStatus(status: string) {
        return omitWarning(() => {
            return this.$t('contract.statuses.' + status)
        })
    }

    getContractCategoryLabel(category: string) {
        return omitWarning(() => {
            return this.$t('contract.category_options.' + category)
        })
    }

    findContractCategoryByLabel(label: string) {
        const categories = this.getAllContractCategoryOptions()
        const keys = _.keys(categories)

        return keys.find(key => (categories[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (categories[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractTermTypeByLabel(label: string) {
        const types = this.getAllContractTermTypeOptions()
        const keys = _.keys(types)

        return keys.find(key => (types[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (types[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findPaymentMethodByLabel(label: string) {
        const options = this.getAllPaymentMethodOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractTermUnitTypeByLabel(label: string) {
        const options = this.getAllContractTermUnitTypeOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractTermRenewalTypeByLabel(label: string) {
        const options = this.getAllContractTermRenewalTypeOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractTermTerminationToByLabel(label: string) {
        const options = this.getAllContractTermTerminationToOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractPaymentTimingByLabel(label: string) {
        const options = this.getAllPaymentTimingOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractPaymentDirectionByLabel(label: string) {
        const options = this.getAllContractPaymentDirectionOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractUsageTypeByLabel(label: string) {
        const options = this.getAllUsageTypeOptions()
        const keys = _.keys(options)

        return keys.find(key => (options[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (options[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    findContractStatusByLabel(label: string) {
        const statuses = this.getAllContractStatuses()
        const keys = _.keys(statuses)

        return keys.find(key => (statuses[key]).toLowerCase() == label.toLowerCase())
            ?? keys.find(key => (statuses[key]).toLowerCase().indexOf(label.toLowerCase()) >= 0)
    }

    getPaymentMethodLabel(paymentMethod: string) {
        return omitWarning(() => {
            return this.$t('payment_options.' + paymentMethod)
        })
    }

    getContractTermRenewalTypeLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.renewal_type_options.' + type)
        })
    }

    getProcurementTypeLabel(type: string) {
        return omitWarning(() => {
            return this.$t('procurement.type_options.' + type)
        })
    }


    getContractTermTerminationToLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.termination_to_options.' + type)
        })
    }


    getPaymentTimingLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.payment_timing_options.' + type)
        })
    }


    getUsageTypeLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.usage_type_options.' + type)
        })
    }

    getContractPaymentDirectionLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.payment_direction_options.' + type)
        })
    }

    getContractTermTypeLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.term_type_options.' + type)
        })
    }

    getContractUnitTypeLabel(type: string) {
        return omitWarning(() => {
            return this.$t('contract.duration_unit_type_options.' + type)
        })
    }

    getAllContractTermTypeOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.term_type_options
    }

    getAllContractLineTypeOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract_line?.type_options
    }

    getAllUsageTypeOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.usage_type_options
    }

    getAllProcurementTypeOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.procurement.type_options
    }

    getAllPaymentTimingOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.payment_timing_options
    }

    getAllContractTermRenewalTypeOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.renewal_type_options
    }

    getAllContractTermUnitTypeOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.duration_unit_type_options
    }

    getAllContractTermTerminationToOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.termination_to_options
    }

    getAllContractCategoryOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.category_options
    }

    getAllPaymentMethodOptions() {
        return _.omit(this.$i18n.messages[this.$i18n.getLocale()]?.payment_options, ['label'])
    }

    getFilteredPaymentMethodOptions() {
        const options = this.getAllPaymentMethodOptions()
        if (this.isMS365ExportActive()) {
            return _.omit(options, ['BANK_TRANSFER_FOREIGN'])
        } else {
            return options
        }
    }

    getAllInvoicePrefixOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.invoice?.invoice_prefix_options
    }

    getAllContractPaymentDirectionOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.payment_direction_options
    }

    getAllContractStatuses() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.contract?.statuses
    }

    getAllCustomFieldTypesOptions() {
        return this.$i18n.messages[this.$i18n.getLocale()]?.custom_field?.type_options
    }

    getCustomFieldTypeLabel(type: string) {
        return omitWarning(() => {
            return this.$t('custom_field.type_options.' + type)
        })
    }

    getDefaultProductTax() {
        return this.configurations?.find((item: any) => item.key == 'product.default.tax')?.value
            || 19
    }

    getDefaultProductCurrency() {
        return 'EUR'
    }

    getDefaultProductUnit() {
        return this.configurations?.find((item: any) => item.key == 'product.default.unit')?.value
            || this.$t('default_unit')
    }

    isEmptyValue(value: any) {
        return ['', null, undefined, []].includes(value) || (isObject(value) && isEmpty(value))
    }

    hasNumericValue(value: any) {
        return value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value))
    }

    roundNumber(number: number, precision: number = 2) {
        return Math.round(number * Math.pow(10, precision)) / Math.pow(10, precision)
    }


    deepAssignNonNull(target: any, patch: any) {
        if (!patch || typeof patch !== 'object') return
        Object.keys(patch).forEach((key) => {
            const value = patch[key]
            if (value === null || value === undefined) return

            const isObj = typeof value === 'object' && !Array.isArray(value)
            if (Array.isArray(value)) {
                try {
                    target[key] = JSON.parse(JSON.stringify(value))
                } catch (e) {
                    target[key] = value
                }
            } else if (isObj) {
                if (!target[key] || typeof target[key] !== 'object' || Array.isArray(target[key])) {
                    target[key] = {}
                }
                this.deepAssignNonNull(target[key], value)
            } else {
                target[key] = value
            }
        })
    }

    confirmBeforeContinue(msg: string, title: string = '', confirmBtn: string = this.$t('procurement.page.default_confirm_btn'), positiveConfirmation: boolean = true) {
        return this.$confirm(msg, {
            confirmButtonText: confirmBtn,
            confirmButtonClass: positiveConfirmation ? 'el-button--primary solid' : 'el-button--danger solid',
            cancelButtonText: this.$t('cancel'),
            dangerouslyUseHTMLString: true,
            title: title,
            center: true,
        }).then(() => {
            return new Promise((resolve: any) => resolve(true));
        }).catch(() => {
            return new Promise((resolve: any) => resolve(false));
        })
    }

    get procurementLabels() {
        return {
            order_number_formatted: this.$t('overview_page.export.labels.procurement.order_number_formatted'),
            name: this.$t('overview_page.export.labels.procurement.name'),
            creator_name: this.$t('overview_page.export.labels.procurement.creator_name'),
            total_price_brutto: this.$t('overview_page.export.labels.procurement.total_price_brutto'),
            total_price_netto: this.$t('overview_page.export.labels.procurement.total_price_netto'),
            created_at: this.$t('overview_page.export.labels.procurement.created_at'),
            client_number: this.$t('overview_page.export.labels.procurement.client_number'),
            client_name: this.$t('overview_page.export.labels.procurement.client_name'),
            department_name: this.$t('overview_page.export.labels.procurement.department_name'),
            department_number: this.$t('overview_page.export.labels.procurement.department_number'),
            cost_center_name: this.$t('overview_page.export.labels.procurement.cost_center_name'),
            cost_center_number: this.$t('overview_page.export.labels.procurement.cost_center_number'),
            ledger_account_name: this.$t('overview_page.export.labels.invoice.ledger_account_name'),
            ledger_account_number: this.$t('overview_page.export.labels.invoice.ledger_account_number'),
            supplier_name: this.$t('overview_page.searchbar.procurement_props.supplier_names'),
            supplier_names: this.$t('overview_page.searchbar.procurement_props.supplier_names'),
            consignee_name: this.$t('overview_page.export.labels.procurement.consignee_name'),
            shipping_date: this.$t('overview_page.export.labels.procurement.shipping_date'),
            shipping_name: this.$t('overview_page.export.labels.procurement.shipping_name'),
            shipping_consignee: this.$t('overview_page.export.labels.procurement.shipping_consignee'),
            shipping_dropoff_location: this.$t('overview_page.export.labels.procurement.shipping_dropoff_location'),
            shipping_address: this.$t('overview_page.export.labels.procurement.shipping_address'),
            shipping_complement: this.$t('overview_page.export.labels.procurement.shipping_complement'),
            shipping_country: this.$t('overview_page.export.labels.procurement.shipping_country'),
            shipping_zipcode: this.$t('overview_page.export.labels.procurement.shipping_zipcode'),
            shipping_city: this.$t('overview_page.export.labels.procurement.shipping_city'),
            shipping_phone_country_code: this.$t('overview_page.export.labels.procurement.shipping_phone_country_code'),
            shipping_phone_number: this.$t('overview_page.export.labels.procurement.shipping_phone_number'),
            billing_name: this.$t('overview_page.export.labels.procurement.billing_name'),
            billing_consignee: this.$t('overview_page.export.labels.procurement.billing_consignee'),
            billing_dropoff_location: this.$t('overview_page.export.labels.procurement.billing_dropoff_location'),
            billing_address: this.$t('overview_page.export.labels.procurement.billing_address'),
            billing_complement: this.$t('overview_page.export.labels.procurement.billing_complement'),
            billing_country: this.$t('overview_page.export.labels.procurement.billing_country'),
            billing_zipcode: this.$t('overview_page.export.labels.procurement.billing_zipcode'),
            billing_city: this.$t('overview_page.export.labels.procurement.billing_city'),
            billing_phone_country_code: this.$t('overview_page.export.labels.procurement.billing_phone_country_code'),
            billing_phone_number: this.$t('overview_page.export.labels.procurement.billing_phone_number'),
            status: this.$t('overview_page.export.labels.procurement.status'),
            payment_in_advance: this.$t('overview_page.searchbar.common_props.payment_in_advance'),
            is_fully_allocated: this.$t('procurement.is_fully_allocated'),
            goods_receipt: this.$t('overview_page.my_orders_goods_receipt'),
            open_value: this.$t('procurement.open_value'),
            invoice_count: this.$t('procurement.invoice_count'),
            has_invoices: this.$t('procurement.has_invoices'),
            current_editor_name: this.$t('overview_page.searchbar.timeline_props.current_editor_name'),
        } as any
    }

    get invoiceLabels() {
        return {
            invoice_number_formatted: this.$t('overview_page.export.labels.invoice.invoice_number_formatted'),
            number: this.$t('overview_page.export.labels.invoice.number'),
            creator_name: this.$t('overview_page.export.labels.invoice.creator_name'),
            procurement_name: this.$t('overview_page.export.labels.invoice.procurement_name'),
            procurement_number: this.$t('overview_page.export.labels.invoice.procurement_number'),
            cost_center_name: this.$t('overview_page.export.labels.invoice.cost_center_name'),
            cost_center_number: this.$t('overview_page.export.labels.invoice.cost_center_number'),
            department_name: this.$t('overview_page.export.labels.procurement.department_name'),
            department_number: this.$t('overview_page.export.labels.procurement.department_number'),
            total_price_netto: this.$t('overview_page.export.labels.invoice.total_price_netto'),
            total_price_brutto: this.$t('overview_page.export.labels.invoice.total_price_brutto'),
            created_at: this.$t('overview_page.export.labels.invoice.created_at'),
            client_name: this.$t('overview_page.export.labels.invoice.client_name'),
            client_number: this.$t('overview_page.export.labels.invoice.client_number'),
            invoice_date: this.$t('overview_page.export.labels.invoice.invoice_date'),
            delivery_date: this.$t('overview_page.export.labels.invoice.delivery_date'),
            due_date: this.$t('overview_page.export.labels.invoice.due_date'),
            currency: this.$t('overview_page.export.labels.invoice.currency'),
            ledger_account_name: this.$t('overview_page.export.labels.invoice.ledger_account_name'),
            ledger_account_number: this.$t('overview_page.export.labels.invoice.ledger_account_number'),
            posting_key_number: this.$t('overview_page.export.labels.invoice.posting_key_number'),
            posting_key_tax: this.$t('overview_page.export.labels.invoice.posting_key_tax'),
            supplier_name: this.$t('overview_page.export.labels.invoice.supplier_name'),
            status: this.$t('overview_page.export.labels.invoice.status'),
            payment_in_advance: this.$t('overview_page.searchbar.common_props.payment_in_advance'),
            paid: this.$t('overview_page.export.labels.invoice.paid'),
            is_final_invoice: this.$t('invoice.is_final_invoice_short'),
            current_editor_name: this.$t('overview_page.searchbar.timeline_props.current_editor_name')
        } as any
    }

    get contractLabels() {
        return {
            contract_number_formatted: this.$t('overview_page.export.labels.contract.contract_number_formatted'),
            name: this.$t('overview_page.export.labels.contract.name'),
            contract_number: this.$t('overview_page.export.labels.contract.contract_number'),
            creator_name: this.$t('overview_page.export.labels.contract.creator_name'),
            cost_center_name: this.$t('overview_page.export.labels.contract.cost_center_name'),
            cost_center_number: this.$t('overview_page.export.labels.contract.cost_center_number'),
            created_at: this.$t('overview_page.export.labels.contract.created_at'),
            client_name: this.$t('overview_page.export.labels.contract.client_name'),
            client_number: this.$t('overview_page.export.labels.contract.client_number'),
            department_name: this.$t('overview_page.export.labels.procurement.department_name'),
            department_number: this.$t('overview_page.export.labels.procurement.department_number'),
            contract_date: this.$t('overview_page.export.labels.contract.contract_date'),
            supplier_name: this.$t('overview_page.export.labels.contract.supplier_name'),
            supplier_number: this.$t('overview_page.export.labels.contract.supplier_number'),
            category: this.$t('overview_page.export.labels.contract.category'),
            term_type: this.$t('overview_page.export.labels.contract.term_type'),
            payment_method: this.$t('overview_page.export.labels.contract.payment_method'),
            status: this.$t('overview_page.export.labels.contract.status'),
            usage_type: this.$t('overview_page.export.labels.contract.usage_type'),
            payment_direction: this.$t('overview_page.export.labels.contract.payment_direction'),
            responsible_name: this.$t('overview_page.export.labels.contract.responsible_name'),
            recurring_invoice: this.$t('overview_page.export.labels.contract.recurring_invoice'),
            start_date: this.$t('overview_page.export.labels.contract.start_date'),
            end_date: this.$t('overview_page.export.labels.contract.end_date'),
            duration: this.$t('overview_page.export.labels.contract.duration'),
            duration_unit_type: this.$t('overview_page.export.labels.contract.duration_unit_type'),
            renewal_type: this.$t('overview_page.export.labels.contract.renewal_type'),
            renewal_duration: this.$t('overview_page.export.labels.contract.renewal_duration'),
            renewal_duration_unit_type: this.$t('overview_page.export.labels.contract.renewal_duration_unit_type'),
            payment_timing: this.$t('overview_page.export.labels.contract.payment_timing'),
            notice_period: this.$t('overview_page.export.labels.contract.notice_period'),
            notice_period_unit_type: this.$t('overview_page.export.labels.contract.notice_period_unit_type'),
            termination_special_right: this.$t('overview_page.export.labels.contract.termination_special_right'),
            termination_deadline: this.$t('overview_page.export.labels.contract.termination_deadline'),
            next_possible_contract_end: this.$t('overview_page.export.labels.contract.next_possible_contract_end'),
            terminated_on: this.$t('overview_page.export.labels.contract.terminated_on'),
            termination_to: this.$t('overview_page.export.labels.contract.termination_to'),
            notes: this.$t('overview_page.export.labels.contract.notes'),
            current_editor_name: this.$t('overview_page.searchbar.timeline_props.current_editor_name'),
        } as any
    }

    getFormattedCustomFields(customFields: any = []) {
        const fieldsValues: any = {}
        customFields?.map((field: any) => {
            fieldsValues[field.custom_field.field_key] = this.getValueFromCustomField(field)
        })
        return fieldsValues
    }

    getValueFromCustomField(customField: any) {
        return (this.isCustomFieldSelectable(customField.custom_field)) ?
            this.getOptionLabelByOptionKey(customField.custom_field, customField.value) :
            this.stripHtml(customField.value)
    }

    getOptionLabelByOptionKey(customField: any, selectedOptionKey: string) {
        const options = this.getOptionsFromCustomField(customField)
        return options[selectedOptionKey]
    }

    isCustomFieldSelectable(customField: any) {
        return ["radio", "enum"].includes(customField.type)
    }

    getOptionsFromCustomField(customField: any) {
        let items: any = {}
        customField.options?.forEach((option: any) => {
            if (!items[option.option_key] || option.language == this.currentLanguage)
                items[option.option_key] = option.label
        })
        return items
    }

    stripHtml(html?: string): string {
        return html?.replace(/<[^>]*>/g, '') ?? ""
    }

    /**START APPROVAL LOGIC*/

    sortApproversConsideringApprovalLimitPriority(approvers: any[]) {
        const creatorOrFallback = approvers.filter((approver: any) => approver.creator_or_fallback_inserted)
        const regularApprovers = approvers.filter((approver: any) => !approver.creator_or_fallback_inserted)

        if (this.shouldConsiderApprovalLimitPriority()) {
            const approvalLimitGroups = regularApprovers.reduce((groups: Record<string, any[]>, approver: any) => {
                const limit = approver.lb_user?.approval_limit ?? '∞'
                if (!groups[limit]) groups[limit] = []
                groups[limit].push(approver)
                return groups
            }, {})

            let approversList: any[] = []
            for (const group of Object.values(approvalLimitGroups)) {
                group.sort((a, b) => (a.lb_user?.priority_approval_limit ?? -1) - (b.lb_user?.priority_approval_limit ?? -1))
                approversList.push(...group)
            }
            return prependCreatorOrFallbackApprovers([...creatorOrFallback, ...approversList], creatorOrFallback)
        }
        return prependCreatorOrFallbackApprovers(approvers, creatorOrFallback)
    }

    sortApprovers(approvers: any[]) {
        const selectApprovalComponent = (this.$refs?.['select-approval'] as InstanceType<typeof SelectUsersGroups>)
        let approversList: any[] = []

        if (!selectApprovalComponent) {
            approversList = _.sortBy(approvers, (item: any) =>
                [null, undefined].includes(item.lb_user?.approval_limit) ?
                    Number.MAX_SAFE_INTEGER : item.lb_user?.approval_limit)
        } else {
            approversList = selectApprovalComponent.getSortedListByApprovalLimit(approvers)
        }

        return this.sortApproversConsideringApprovalLimitPriority(approversList)
    }

    async getApproversConsideringLimit(approvers: any, total: any, isCurrentEntryClearing: boolean, invoiceCurrency: string = 'EUR') {
        const creatorOrFallbackApprovers = approvers?.filter((it: any) => it.creator_or_fallback_inserted) ?? []
        const configuredApprovers = approvers?.filter((it: any) => !it.creator_or_fallback_inserted) ?? []
        const requiredApprovers = configuredApprovers.filter((it: any) => it.required || it.manually_inserted || (it.group_inserted && !isCurrentEntryClearing))
        const optionalApprovers = this.sortApprovers(configuredApprovers.filter((it: any) => !requiredApprovers.includes(it)))
        let approversList = [...requiredApprovers]


        const hasLimitEnough = await this.hasAtLeastOneApproverWithApprovalLimit(approversList, total, invoiceCurrency)
        if (!hasLimitEnough)
            for (const approver of optionalApprovers) {
                if (!approver.creator_or_fallback_inserted) approver.required = true
                approversList.push(approver)
                if (await this.hasAtLeastOneApproverWithApprovalLimit([approver], total, invoiceCurrency)) break
            }

        return creatorOrFallbackApprovers.concat(this.sortApproversConsideringApprovalLimitPriority(approversList))
    }

    /**
     * Per-entity approval source with optional max_approvers (PROCURE-2086).
     * Checkbox-required counts toward max; cascade/amount-promoted required do not.
     * Caller should use this only when max is configured; without max it returns
     * cascade-only (not a substitute for the legacy markAsRequired path).
     */
    async resolveApproverSourceWithMax(
        approvers: any,
        maxApprovers: number | null | undefined,
        amount: number,
        isCurrentEntryClearing: boolean,
        invoiceCurrency: string = 'EUR'
    ) {
        let list: any[] = _.cloneDeep(approvers) ?? []
        if (!list.length) return []

        const originalRequiredSnapshot = list.map((a: any) => ({
            lb_user_id: a.lb_user_id,
            required: !!a.required,
        }))

        list = cascadeRequiredByApprovalLimit(list)
        if (!isMaxApproversConfigured(maxApprovers)) return list

        const amountNum = Number(amount)
        const adequate = Number.isFinite(amountNum) && amountNum >= 0
            ? await this.getApproversConsideringLimit(
                list,
                amountNum,
                isCurrentEntryClearing,
                invoiceCurrency
            )
            : list

        const capped = capApproversAfterLimitCheck(originalRequiredSnapshot, adequate, maxApprovers, amountNum)
        return capped
    }

    async hasAtLeastOneApproverWithApprovalLimit(approvers: any = null, total: any, invoiceCurrency: string = 'EUR') {
        const selectApprovalComponent = (this.$refs?.['select-approval'] as InstanceType<typeof SelectUsersGroups>)
        if (!selectApprovalComponent) return false

        const anyoneInfinite = selectApprovalComponent.hasAnyoneInfiniteLimit(approvers)
        if (anyoneInfinite) return true

        const maxLimitInfo = await selectApprovalComponent.getMaxApprovalLimitWithCurrency(approvers, invoiceCurrency)
        return maxLimitInfo.convertedValue >= total
    }

    sortApproversWithTimelineApprovalEntries(approvers?: any[], approvalEntries?: any[]) {
        const selectApprovalComponent = (this.$refs?.['select-approval'] as InstanceType<typeof SelectUsersGroups>)

        const orderMap = new Map<number, number>()
        approvalEntries?.forEach((entry: any, index: number) => {
            orderMap.set(entry.lb_user_id, index + 1)
        })

        if (!selectApprovalComponent) {
            const sorted = _.sortBy(approvers, (item: any) => {
                const approvalLimit = [null, undefined].includes(item.lb_user?.approval_limit) ?
                    Number.MAX_SAFE_INTEGER : item.lb_user?.approval_limit
                const timelineOrder = orderMap.get(item.lb_user_id)
                return [timelineOrder ?? Number.MAX_SAFE_INTEGER, approvalLimit]
            })
            return prependCreatorOrFallbackApprovers(
                sorted,
                (approvers ?? []).filter((approver: any) => approver.creator_or_fallback_inserted)
            )
        }

        return selectApprovalComponent.getSortedListByApprovalLimitAndTimelineOrder(approvers, orderMap)
    }

    /**END APPROVAL LOGIC*/

    execute(fn: any, ...args: any[]) {
        this.focusBtnSave()
        setTimeout(() => {
            fn(...args)
        }, 0)
    }

    getBudgetLabel(budget: any, showTotal: boolean = false) {
        let label = this.getBudgetName(budget, showTotal)
        if (budget.end) return label + ' - ' + this.lbDate(budget.start) + ' - ' + this.lbDate(budget.end)
        else return label + ' - ' + this.$t('dashboard_page.budgets.from') + ' ' + this.lbDate(budget.start)
    }

    getBudgetName(budget: any, showTotal: boolean = false) {
        const hasName = !!budget.name || !!budget?.ledger_account?.name
        let name = budget.name || budget?.ledger_account?.name
        if (hasName)
            if (showTotal) return `${name} | ${this.getCurrencyFormat(budget.value, undefined, true)}`
            else return name
        return this.getCurrencyFormat(budget.value, undefined, true)
    }

    sanitizeFileName(filename: string): string {
        const nameWithoutExtension = filename.replace(/\.[^/.]+$/, "")
        return this.sanitizeString(nameWithoutExtension)
    }

    sanitizeString(text: string): string {
        const cleaned = text
            .replace(/[\\\/:*?"<>|,]/g, "_")
            .replace(/\s+/g, "_")
            .replace(/[^a-zA-Z0-9_]/g, "")
            .toLowerCase()

        const capitalized = cleaned
            .split("_")
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join("_")

        return capitalized
    }

    async getXmlFromBlob(blob: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = () => reject(new Error('Error when reading xml from blob.'));
            reader.readAsText(blob)
        })
    }

    async getXlsFromBlob(blob: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e: any) => {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, {type: 'array'})
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {header: 1})

                const headers: any = jsonData[0]
                const rows = jsonData.slice(1)

                const formattedData = rows.map((row: any) => {
                    return row.reduce((obj: any, cell: any, index: number) => {
                        obj[headers[index]] = cell
                        return obj
                    }, {})
                });

                resolve([headers, ...formattedData])
            }
            reader.onerror = () => reject(new Error('Error when reading xml from blob.'));
            reader.readAsArrayBuffer(blob)
        })
    }

    async processXmlBlob(blob: any) {
        try {
            const xmlContent = await this.getXmlFromBlob(blob)
            const parser = new DOMParser()
            const xmlDoc = parser.parseFromString(xmlContent as string, 'application/xml')
            const parserError = xmlDoc.querySelector('parsererror')
            if (parserError) console.log('Error when processing xml from blob. ' + parserError.textContent)
            return xmlContent
        } catch (err) {
            console.error('Erro:', err)
            return null
        }
    }

    async processXlsBlob(blob: any) {
        try {
            return await this.getXlsFromBlob(blob)
        } catch (err) {
            console.error('Erro:', err)
            return null
        }
    }

    focusBtnSave() {
        document.getElementById('btn-save')?.focus()
    }
}

export class GqlMutationWithData {
    constructor(
        public gql: any,
        public data: any
    ) {
    }
}

export interface ClientFormFieldConfig {
    client_id?: number
    form: string
    field: string
    required: boolean
    visible?: boolean
}
